import "./style.css";
import { Store } from "./state/store";
import { AudioEngine } from "./audio/engine";
import { RecorderManager, listInputs, listOutputs, InputDevice, MicProcessing, REMOTE_INPUT, createCapture } from "./audio/recorder";
import { Timeline } from "./ui/timeline";
import { Monitor } from "./ui/monitor";
import { MediaBin } from "./ui/media-bin";
import { Mixer } from "./ui/mixer";
import { openModal, toast } from "./ui/modal";
import { openFxEditor } from "./ui/fx-editor";
import { openSpeedDialog, openTransitionDialog } from "./ui/clip-inspector";
import { InspectorPanel } from "./ui/inspector-panel";
import { openGenerateDialog } from "./ui/generate-dialog";
import type { NewItemKind } from "./ui/media-bin";
import { el, uid, fmtTimecode } from "./util";
import { newClip, MediaAsset, Effect, RecordMode, RECORD_MODE_LABELS } from "./state/model";
import { makeEffect, EFFECT_LABELS, DENOISE_WORKLET_URL, DEVERB_WORKLET_URL } from "./audio/effects";
import { saveProject, loadProject, listProjects, deleteProject } from "./state/storage";
import { downloadBlob } from "./io/export-wav";
import { aacSupported, exportM4a, exportWavMix } from "./io/export-audio";
import { BlobSink, FileSink, ExportSink, pickSaveFile } from "./io/sink";
import { readZip, zipEntryBytes } from "./io/zip";
import type { WebcutLoadReport } from "./io/webcut-project";
import { exportDawproject, importDawproject } from "./io/dawproject";
import { exportWebcut, importWebcut } from "./io/webcut-project";
import { exportStems } from "./io/export-stems";
import { exportVideo, videoExportSupported, exportSizes, FRAME_RATES, frameRateFor, contentDuration, ExportQuality } from "./io/export-video";
import { loadWamEffect, openWamGui, reconcileWam } from "./audio/wam";
import { calibrateLatency } from "./audio/calibrate";
import { RemoteSession, jbBetween } from "./sync/rtc";
import { VideoSource } from "./video/source";
import { extractAudioBuffers, demuxMp4Audio } from "./video/demux-audio";
import { demuxMatroskaAudio } from "./video/demux-matroska";

const store = new Store();
const engine = new AudioEngine(store);
const recorder = new RecorderManager(engine);
let devices: InputDevice[] = [];
let recordStartPos = 0;
let lastArmSig = "";
// Manual record-latency offset (ms), device-specific → persisted in localStorage.
let recOffsetMs = Number(localStorage.getItem("webcut.recOffsetMs") || 0);
const remote = new RemoteSession(store, engine);

// ---- layout ----------------------------------------------------------
const app = el("div", { class: "app" });
const topbar = el("div", { class: "topbar" });
const main = el("div", { class: "main" });
app.append(topbar, main);
document.getElementById("app")!.append(app);

const monitor = new Monitor(store);
const mediaBin = new MediaBin(store, {
  addToTimeline: addAssetToTimeline,
  deleteAsset: deleteMediaAsset,
  newItem: (kind) => newGeneratedItem(kind),
  editItem: (id) => openGenerateDialog(store, { editAssetId: id }),
  importMedia: () => openImportMenu(),
});
const inspector = new InspectorPanel(store, engine);
const timeline = new Timeline(store, engine, {
  editClip: () => showSideTab("inspector"),
  editSpeed: (clipId) => openSpeedDialog(store, clipId),
  editTransition: (clipId) => openTransitionDialog(store, clipId),
  showShortcuts: () => openShortcuts(),
});

// Left panel: Media bin | Inspector tabs (NLE-style project/inspector area).
const sidePanel = el("div", { class: "side-panel" });
const sideTabs = el("div", { class: "side-tabs" });
const tabBtns: Record<"media" | "inspector", HTMLButtonElement> = {
  media: el("button", { class: "side-tab", text: "Media" }) as HTMLButtonElement,
  inspector: el("button", { class: "side-tab", text: "Inspector" }) as HTMLButtonElement,
};
sideTabs.append(tabBtns.media, tabBtns.inspector);
sidePanel.append(sideTabs, mediaBin.el, inspector.el);
function showSideTab(tab: "media" | "inspector") {
  for (const k of ["media", "inspector"] as const) tabBtns[k].classList.toggle("on", k === tab);
  mediaBin.el.style.display = tab === "media" ? "" : "none";
  inspector.el.style.display = tab === "inspector" ? "" : "none";
  inspector.setVisible(tab === "inspector");
  try { localStorage.setItem("webcut.sideTab", tab); } catch {}
}
tabBtns.media.addEventListener("click", () => showSideTab("media"));
tabBtns.inspector.addEventListener("click", () => showSideTab("inspector"));
showSideTab((() => { try { return localStorage.getItem("webcut.sideTab") === "inspector" ? "inspector" : "media"; } catch { return "media"; } })());
const mixer = new Mixer(store, engine, {
  openFx: (target, fxId) => openFxEditor(store, engine, target, fxId, (id) => guiForWam(target, id)),
  addFx: (target) => addFxPicker(target),
});

// Layout: top row = media bin | Program monitor; bottom row = timeline | mixer.
// Each split is drag-resizable (persisted).
const topRow = el("div", { class: "top-row" });
const mediaResizer = el("div", { class: "col-resizer", attrs: { title: "Drag to resize the media panel" } });
topRow.append(sidePanel, mediaResizer, monitor.el);
const bottomRow = el("div", { class: "bottom-row" });
const mixerResizer = el("div", { class: "col-resizer", attrs: { title: "Drag to resize the mixer" } });
bottomRow.append(timeline.el, mixerResizer, mixer.el);
const rowResizer = el("div", { class: "row-resizer", attrs: { title: "Drag to resize the top/bottom split" } });
main.append(topRow, rowResizer, bottomRow);
timeline.mount();

const clampN = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
function addAssetToTimeline(assetId: string) { placeAssetOnTimeline(assetId); }
/** Delete a media item — confirm first if it's currently used on the timeline. */
function deleteMediaAsset(assetId: string) {
  const asset = store.asset(assetId);
  if (!asset) return;
  if (store.assetInUse(assetId)) {
    openModal(`Remove “${asset.name}”?`, el("p", { class: "hint", text: "This media is on the timeline. Removing it deletes those clips too." }), [
      { label: "Cancel", onClick: () => {} },
      { label: "Remove", primary: true, onClick: () => store.removeAsset(assetId) },
    ]);
  } else {
    store.removeAsset(assetId);
  }
}
function bindHResize(handle: HTMLElement, getW: () => number, setW: (w: number) => void, sign: number) {
  handle.addEventListener("mousedown", (e) => {
    e.preventDefault();
    const startX = e.clientX, startW = getW();
    document.body.style.cursor = "col-resize";
    const move = (ev: MouseEvent) => setW(startW + sign * (ev.clientX - startX));
    const up = () => { document.body.style.cursor = ""; window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  });
}
function bindVResize(handle: HTMLElement, getH: () => number, setH: (h: number) => void) {
  handle.addEventListener("mousedown", (e) => {
    e.preventDefault();
    const startY = e.clientY, startH = getH();
    document.body.style.cursor = "row-resize";
    const move = (ev: MouseEvent) => setH(startH + (ev.clientY - startY));
    const up = () => { document.body.style.cursor = ""; window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  });
}
function initLayout() {
  const mainH = main.clientHeight || 600;
  topRow.style.height = clampN(Number(localStorage.getItem("webcut.topH")) || Math.round(mainH * 0.45), 140, mainH - 160) + "px";
  const topW = topRow.clientWidth || 1000;
  sidePanel.style.width = clampN(Number(localStorage.getItem("webcut.mediaW")) || Math.round(topW * 0.35), 140, topW * 0.7) + "px";
  const botW = bottomRow.clientWidth || 1000;
  mixer.el.style.width = clampN(Number(localStorage.getItem("webcut.mixerW")) || Math.round(botW * 0.25), 160, botW * 0.6) + "px";
}
bindHResize(mediaResizer, () => sidePanel.offsetWidth, (w) => { const v = clampN(w, 140, topRow.clientWidth * 0.7); sidePanel.style.width = v + "px"; localStorage.setItem("webcut.mediaW", String(Math.round(v))); }, +1);
bindHResize(mixerResizer, () => mixer.el.offsetWidth, (w) => { const v = clampN(w, 160, bottomRow.clientWidth * 0.6); mixer.el.style.width = v + "px"; localStorage.setItem("webcut.mixerW", String(Math.round(v))); }, -1);
bindVResize(rowResizer, () => topRow.offsetHeight, (h) => { const v = clampN(h, 140, main.clientHeight - 160); topRow.style.height = v + "px"; localStorage.setItem("webcut.topH", String(Math.round(v))); });
requestAnimationFrame(initLayout);

// Media bin: dropping files adds them to Media (no timeline placement).
enableDrop(sidePanel, (files) => { showSideTab("media"); for (const f of files) importFileToMedia(f); });
// Timeline: dropping files imports + places them; dragging a media-bin asset places
// it. The drop position chooses the time and the target track (Video N → Audio N).
enableDrop(timeline.el,
  (files, e) => importFilesToTimeline(files, timeline.timeAtClientX(e.clientX), timeline.trackAtClientY(e.clientY)),
  (assetId, e) => placeAssetOnTimeline(assetId, timeline.timeAtClientX(e.clientX), timeline.trackAtClientY(e.clientY)));
// Don't let a stray drop elsewhere navigate the page away.
window.addEventListener("dragover", (e) => e.preventDefault());
window.addEventListener("drop", (e) => e.preventDefault());

// ---- top bar ---------------------------------------------------------
const brand = el("div", {
  class: "brand",
  attrs: { title: "WebCut — video editor" },
  html: `
    <svg class="brand-mark" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="wcmark" x1="2" y1="2" x2="30" y2="30" gradientUnits="userSpaceOnUse">
          <stop stop-color="#6ea0ff"/><stop offset="1" stop-color="#9b6bff"/>
        </linearGradient>
      </defs>
      <rect x="1.25" y="1.25" width="29.5" height="29.5" rx="8.5" fill="#131826" stroke="url(#wcmark)" stroke-opacity=".55" stroke-width="1.5"/>
      <!-- film sprockets + play head: a clapboard/play hybrid -->
      <g fill="url(#wcmark)">
        <rect x="5" y="6" width="2.4" height="2.4" rx=".6"/>
        <rect x="5" y="23.6" width="2.4" height="2.4" rx=".6"/>
        <rect x="24.6" y="6" width="2.4" height="2.4" rx=".6"/>
        <rect x="24.6" y="23.6" width="2.4" height="2.4" rx=".6"/>
        <path d="M13 10.5 L23 16 L13 21.5 Z"/>
      </g>
    </svg>
    <span class="brand-text">
      <span class="brand-name"><span class="web">Web</span><span class="cut">Cut</span></span>
      <span class="brand-tag">Video Editor</span>
    </span>`,
});

const projName = el("input", { attrs: { title: "Project name" } }) as HTMLInputElement;
projName.value = store.project.name;
projName.style.width = "150px";
projName.addEventListener("change", () => { store.project.name = projName.value; store.emitChange("project"); });

const fileBtn = btn("File ▾", openFileMenu);
const importBtn = btn("Import ▾", openImportMenu);
const exportBtn = btn("Export ▾", openExportMenu);

// transport (video-focused: frame stepping around play)
const frameStep = (dir: number) => { const f = 1 / (store.project.fps || 30); engine.seek(Math.max(0, (Math.round(engine.position() / f) + dir) * f)); };
const tBack = btn("⏮", () => engine.seek(0)); tBack.title = "Go to start (Home)";
const tPrev = btn("◀ǀ", () => frameStep(-1)); tPrev.title = "Previous frame";
const tPlay = btn("▶", togglePlay);
const tNext = btn("ǀ▶", () => frameStep(1)); tNext.title = "Next frame";
const tStop = btn("⏹", stopTransport);
const tRec = btn("⏺", toggleRecord); tRec.classList.add("rec"); tRec.title = "Record (R) — onto the armed track: camera on a video track, mic on an audio track";
const tCam = btn("🎥", toggleVideoRecord); tCam.classList.add("rec"); tCam.title = "Record video from your camera";
tPlay.title = "Play / pause (Space, or J K L)";
tStop.title = "Stop and return to start";
const tEnd = btn("⏭", () => engine.seek(projectEnd())); tEnd.title = "Go to end (End)";

const loopBtn = btn("🔁", () => {
  store.project.loopEnabled = !store.project.loopEnabled;
  store.emitChange("transport");
  engine.refreshLoop(); // apply immediately if already playing
});
loopBtn.classList.add("toggle"); loopBtn.classList.toggle("on", store.project.loopEnabled);
loopBtn.title = "Loop playback: repeats In to Out when both are marked, otherwise the whole program";

// Monitor-style edit buttons: In / Out, marker, lift / extract.
const tIn = btn("⇤ In", () => markAt("in")); tIn.title = "Mark In at the playhead (I)";
const tOut = btn("Out ⇥", () => markAt("out")); tOut.title = "Mark Out at the playhead (O)";
const tMarker = btn("◆", () => { store.addMarker(engine.position()); toast("Marker added (M)."); }); tMarker.title = "Add a marker at the playhead (M)";
const tLift = btn("Lift", () => liftOrExtract(false)); tLift.title = "Lift: remove In→Out from every track, leaving a gap (;)";
const tExtract = btn("Extract", () => liftOrExtract(true)); tExtract.title = "Extract: remove In→Out from every track and close the gap (')";
const tDissolve = btn("⧓ Dissolve", addTransitionAtPlayhead); tDissolve.title = "Add a 1 s cross-dissolve at the cut nearest the playhead (Ctrl+T) — on the selected clips' tracks, else every track. Audio cuts there get a crossfade.";
monitor.controls.append(
  el("div", { class: "group transport" }, [tBack, tPrev, tPlay, tNext, tEnd, tStop]),
  el("div", { class: "group transport" }, [loopBtn, tRec, tCam]),
  el("div", { class: "spacer" }),
  el("div", { class: "group marks" }, [tDissolve, tIn, tOut, tMarker, tLift, tExtract]),
);

/** Default transition at the cut nearest the playhead (within 1 s). */
function addTransitionAtPlayhead() {
  const trackIds = store.selectedClipIds.size ? [...new Set(store.selectedClipList().map((x) => x.track.id))] : undefined;
  const cut = store.addTransitionNear(engine.position(), trackIds);
  if (cut == null) toast("No cut within 1 s of the playhead — put two clips end to end, then try again (Ctrl+T).", 5000);
  else toast("Cross-dissolve added — drag its edges to change the length, or double-click it.");
}

/** Latest clip end on the timeline (the real end, not the min-16s sizing length). */
function projectEnd(): number {
  let end = 0;
  for (const t of store.project.tracks) for (const c of t.clips) if (c.takeActive !== false) end = Math.max(end, c.start + c.duration);
  return end;
}
function markAt(which: "in" | "out") {
  store.setMark(which, engine.position());
}
function liftOrExtract(ripple: boolean) {
  const { markIn, markOut } = store.project;
  if (markIn == null || markOut == null || markOut - markIn < 1e-3) { toast("Set both In (I) and Out (O) first."); return; }
  store.removeRange(markIn, markOut, ripple);
  if (ripple) { store.setMark("in", undefined); store.setMark("out", undefined); engine.seek(markIn); }
  toast(ripple ? "Extracted In→Out (gap closed)." : "Lifted In→Out (gap left).");
}

const addVideoBtn = btn("+ Video", () => store.createTrack("video"));
const addAudioBtn = btn("+ Audio", () => store.createTrack("audio"));
const delTrackBtn = btn("− Track", openDeleteTracksDialog);

// Pick which track(s) to delete before removing anything (no silent delete).
function openDeleteTracksDialog() {
  const tracks = store.project.tracks;
  if (!tracks.length) { toast("No tracks to delete."); return; }
  const checks = new Map<string, HTMLInputElement>();
  const list = el("div", { class: "trk-del-list" });
  for (const t of tracks) {
    const cb = el("input", { attrs: { type: "checkbox" } }) as HTMLInputElement;
    if (t.id === store.selectedTrackId) cb.checked = true; // default: the selected track
    checks.set(t.id, cb);
    const sw = el("span", { class: "trk-del-swatch" }); sw.style.background = t.color;
    const kindLabel = t.kind === "video" ? "video" : t.kind === "instrument" ? "MIDI" : "audio";
    const clips = t.clips.length ? `${t.clips.length} clip${t.clips.length > 1 ? "s" : ""}` : "empty";
    const row = el("label", { class: "trk-del-row" }, [cb, sw, el("span", { class: "trk-del-name", text: t.name }), el("span", { class: "trk-del-meta hint", text: `${kindLabel} · ${clips}` })]);
    list.append(row);
  }
  const body = el("div", { class: "trk-del" }, [el("p", { class: "hint", text: "Select the track(s) to delete. This can be undone (Ctrl+Z)." }), list]);
  openModal("Delete tracks", body, [
    { label: "Cancel", onClick: () => {} },
    { label: "Delete selected", primary: true, onClick: () => {
      const ids = [...checks].filter(([, cb]) => cb.checked).map(([id]) => id);
      if (!ids.length) { toast("No tracks selected."); return false; } // keep dialog open
      store.removeTracks(ids);
      toast(`Deleted ${ids.length} track${ids.length > 1 ? "s" : ""}.`);
    } },
  ]);
}

const undoBtn = btn("↶", () => store.undo()); undoBtn.title = "Undo (Ctrl+Z)";
const redoBtn = btn("↷", () => store.redo()); redoBtn.title = "Redo (Ctrl+Y)";

// Tools, snapping, ruler units, zoom + track height live in the timeline's own toolbar.

const recModeSel = el("select", { attrs: { title: "Record mode: how a new take interacts with existing audio on a track (project-wide)" } }) as HTMLSelectElement;
for (const m of ["comp", "sos", "overwrite"] as RecordMode[]) {
  const o = el("option", { text: RECORD_MODE_LABELS[m], attrs: { value: m } }) as HTMLOptionElement;
  if ((store.project.recordMode ?? "comp") === m) o.selected = true;
  recModeSel.append(o);
}
recModeSel.value = store.project.recordMode ?? "comp";
recModeSel.addEventListener("change", () => { store.project.recordMode = recModeSel.value as RecordMode; store.emitChange("project"); });
const recModeGroup = el("div", { class: "group" }, [el("span", { class: "hint", text: "Rec" }), recModeSel]);

// Mic processing: Raw (studio, default) vs Noise-reduced. Chrome on Windows
// captures in WASAPI raw mode, which skips the driver/OS noise suppression and
// mic-array processing other editors on the default Windows path get for free.
// Applies to audio-track takes and camera-take audio alike.
const micProcSel = el("select", { attrs: { title: "Mic processing — Raw: unprocessed capture (bypasses Windows/driver noise suppression). Noise-reduced: applies noise suppression (Windows Voice Focus where supported, else the browser's) for less room noise." } }) as HTMLSelectElement;
for (const [v, label] of [["nr", "Noise-reduced"], ["raw", "Raw"]] as const) micProcSel.append(el("option", { text: label, attrs: { value: v } }));
const savedProc = (() => { try { return localStorage.getItem("webcut.micProcessing"); } catch { return null; } })();
micProcSel.value = savedProc === "raw" ? "raw" : "nr"; // Noise-reduced by default; Raw only if explicitly chosen
recorder.processing = micProcSel.value as MicProcessing;
micProcSel.addEventListener("change", () => {
  try { localStorage.setItem("webcut.micProcessing", micProcSel.value); } catch {}
  recorder.processing = micProcSel.value as MicProcessing;
  recorder.disposeAll(); recorder.releaseStreams(); // reopen the mic with the new constraints
  lastArmSig = ""; void refreshArm();
});
/** getUserMedia audio constraints for the current Mic processing mode. */
function micAudioConstraints(): MediaTrackConstraints {
  const nr = recorder.processing === "nr";
  return { echoCancellation: false, noiseSuppression: nr, autoGainControl: false };
}

const recOffsetIn = el("input", { attrs: { type: "number", step: "5", title: "Recording latency offset (ms). Increase if recorded takes land late (behind the beat); decrease if early." } }) as HTMLInputElement;
recOffsetIn.style.width = "54px";
recOffsetIn.value = String(recOffsetMs);
recOffsetIn.addEventListener("change", () => { setRecOffset(Number(recOffsetIn.value) || 0); });
const calibBtn = btn("⇄ Calibrate", runCalibration);
calibBtn.title = "Auto-measure recording latency: plays clicks through your speakers and records them back. Use speakers (not headphones).";

// Camera record resolution (requested via getUserMedia constraints).
const REC_QUALITIES: Record<string, [number, number]> = { "480p": [854, 480], "720p": [1280, 720], "1080p": [1920, 1080], "4K": [3840, 2160] };
let recQuality = localStorage.getItem("webcut.recQuality") || "1080p";
const recQualitySel = el("select", { attrs: { title: "Camera recording resolution" } }) as HTMLSelectElement;
for (const k of Object.keys(REC_QUALITIES)) { const o = el("option", { text: k, attrs: { value: k } }) as HTMLOptionElement; if (k === recQuality) o.selected = true; recQualitySel.append(o); }
recQualitySel.addEventListener("change", () => { recQuality = recQualitySel.value; localStorage.setItem("webcut.recQuality", recQuality); });

// Recording settings live in a menu (this app is edit-first), not the top bar.
const recSettingsBtn = btn("⚙ Recording", openRecSettings);
recSettingsBtn.title = "Recording settings: camera quality, record mode, latency, calibration";
function recRow(label: string, ...controls: HTMLElement[]) { return el("div", { class: "fx-param" }, [el("label", { text: label }), ...controls]); }
function openRecSettings() {
  const body = el("div", { class: "fx-editor" }, [
    recRow("Camera quality", recQualitySel),
    recRow("Record mode", recModeSel),
    recRow("Mic", micProcSel),
    recRow("Latency (ms)", recOffsetIn),
    el("div", { class: "clip-actions" }, [calibBtn]),
    el("p", { class: "hint", text: "Camera quality is what WebCut requests from your camera (it uses the closest mode available). Mic: Raw is unprocessed; Noise-reduced lowers room noise (Chrome otherwise bypasses Windows' own mic noise suppression). Calibrate measures audio recording latency (plays clicks through your speakers and records them back)." }),
  ]);
  openModal("Recording settings", body, [{ label: "Done", primary: true, onClick: () => {} }]);
}

// Devices chosen in the failure dialog; default (null) = system default.
let calibInputId: string | null = null;
let calibOutputId: string | null = null;

async function runCalibration() {
  try {
    if (engine.playing || engine.recording) engine.stop();
    await engine.resume();
    await engine.ctx.audioWorklet.addModule("/recorder-worklet.js").catch(() => {});
    toast("Calibrating… you'll hear 3 beeps — keep quiet, use speakers (not headphones).", 4000);
    const r = await calibrateLatency(engine.ctx, { inputDeviceId: calibInputId, outputDeviceId: calibOutputId });
    if ("error" in r) { showCalibrationDevicePicker(r.error); return; }
    const auto = (engine.ctx.outputLatency || engine.ctx.baseLatency || 0) + recorder.inputLatency();
    setRecOffset(Math.max(0, Math.round(r.roundTripMs - auto * 1000)));
    toast(`Heard ${r.samples}/3 clicks on “${r.inputLabel}” (round-trip ${r.roundTripMs} ms) → latency offset set to ${recOffsetMs} ms.`, 7000);
  } catch (e: any) {
    console.error("calibration failed", e);
    showCalibrationDevicePicker("Calibration failed: " + (e?.message || e));
  }
}

/** Calibration couldn't hear the clicks — the default mic/output may be wrong
 *  (e.g. listening on a headset mic, or playing to headphones). Let the user
 *  pick the input and output to use, then retry. */
async function showCalibrationDevicePicker(errMsg: string) {
  let inputs: InputDevice[] = [], outputs: InputDevice[] = [];
  try { inputs = await listInputs(); } catch {}
  try { outputs = await listOutputs(); } catch {}

  const mk = (devs: InputDevice[], current: string | null) => {
    const sel = el("select") as HTMLSelectElement;
    sel.append(el("option", { text: "System default", attrs: { value: "" } }));
    for (const d of devs) {
      const o = el("option", { text: d.label, attrs: { value: d.deviceId } }) as HTMLOptionElement;
      if (d.deviceId === current) o.selected = true;
      sel.append(o);
    }
    return sel;
  };
  const labeled = (label: string, ctrl: HTMLElement) => el("div", { class: "fx-param" }, [el("label", { text: label }), ctrl]);
  const inSel = mk(inputs, calibInputId);
  const body = el("div", { class: "fx-editor" }, [
    el("p", { text: errMsg }),
    el("p", { class: "hint", text: "Pick the mic that can hear your speakers and the speakers to play from, then retry. Use real speakers — headphones won't reach the mic." }),
    labeled("Microphone", inSel),
  ]);
  let outSel: HTMLSelectElement | null = null;
  if (outputs.length && typeof (engine.ctx as any).setSinkId === "function") {
    outSel = mk(outputs, calibOutputId);
    body.append(labeled("Output", outSel));
  }
  openModal("Choose calibration devices", body, [
    { label: "Cancel", onClick: () => {} },
    { label: "⇄ Retry calibration", primary: true, onClick: () => {
      calibInputId = inSel.value || null;
      if (outSel) calibOutputId = outSel.value || null;
      runCalibration();
    } },
  ]);
}

const syncBtn = btn("⇄ Remote", openRemoteMenu);
const syncBadge = el("span", { class: "badge", text: "offline" });

topbar.append(
  brand, projName, fileBtn, importBtn, exportBtn,
  sep(), undoBtn, redoBtn,
  sep(), addVideoBtn, addAudioBtn, delTrackBtn,
  sep(), recSettingsBtn,
  el("div", { class: "spacer" }),
  syncBtn, syncBadge,
);

function btn(label: string, on: () => void): HTMLButtonElement {
  const b = el("button", { text: label }); b.addEventListener("click", on);
  // Don't take keyboard focus on a mouse click: a focused button would also
  // "click" on Space, so Space-to-play would toggle twice. Tab still focuses it.
  b.addEventListener("mousedown", (e) => e.preventDefault());
  return b;
}
function sep() { return el("div", { class: "sep" }); }

// ---- transport / record ---------------------------------------------
async function togglePlay() {
  if (engine.recording) { await finishRecord(); return; } // pause/space during record = finalize take
  await engine.resume();
  if (engine.playing) engine.pause(); else engine.play();
}

async function stopTransport() {
  if (engine.recording) await finishRecord(); // materialize the take before resetting
  engine.stop();
}

/** Armed AUDIO tracks (what the audio recorder captures). Video tracks are armed
 *  for camera recording instead — they must never get a mic take. */
function armedTracks() {
  return store.project.tracks.filter((t) => t.armed && t.kind !== "video").map((t) => ({ id: t.id, deviceId: t.inputDeviceId, monitor: t.monitor, inputGain: t.inputGain ?? 0, inputChannel: t.inputChannel }));
}

function micError(e: any): string {
  const name = e?.name || "";
  if (name === "RemotePermissionError") return `${e.message} Ask for permission in ⇄ Remote → Recording.`;
  if (name === "NotAllowedError" || name === "SecurityError") return "Microphone blocked. Allow mic access in your browser's site settings (the 🎤/🔒 icon in the address bar), then try again.";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "No microphone found for the selected input. Pick a different input device on the track.";
  if (name === "NotReadableError") return "Microphone is in use by another app. Close it and try again.";
  return `Could not access the microphone (${name || "unknown error"}).`;
}

async function refreshArm() {
  const armed = armedTracks();
  const sig = armed.map((a) => `${a.id}:${a.deviceId}:${a.monitor}:${a.inputChannel ?? "1"}`).join("|");
  if (sig === lastArmSig) return;
  lastArmSig = sig;
  if (armed.length === 0) { recorder.disposeAll(); recorder.releaseStreams(); return; }
  // Arming implies intent to record/monitor — only now do we touch the mic and
  // populate the real device list (no permission prompt happens before this).
  ensureInputDevices();
  // Pre-arm for input monitoring/metering. Failures here are non-fatal; the real
  // gate is at record time, so keep this quiet (no scary toast while just arming).
  try { await recorder.arm(armed); }
  catch (e) { console.warn("pre-arm failed (will retry at record):", e); lastArmSig = ""; }
}

// Enumerate audio inputs lazily (first call triggers the mic permission prompt).
let inputDevicesLoaded = false;
async function ensureInputDevices() {
  if (inputDevicesLoaded) return;
  inputDevicesLoaded = true;
  try { devices = await listInputs(); pushDevices(); }
  catch { inputDevicesLoaded = false; }
}
/** Input choices for audio tracks: local devices, plus the partner's mic while
 *  they've allowed recording in the current remote session. */
function pushDevices() {
  const remoteOk = remote.remoteGrant === "granted";
  timeline.setDevices(remoteOk ? [...devices, { deviceId: REMOTE_INPUT, label: "🌐 Partner's mic (remote)" } as InputDevice] : devices);
}

let warnedInputDsp = false;
function warnIfInputProcessed() {
  if (warnedInputDsp) return;
  const p = recorder.inputProcessing();
  if (p && (p.agc || (p.ns && recorder.processing === "raw"))) {
    warnedInputDsp = true;
    toast("Heads up: your mic's auto-gain/noise-suppression is on (forced by the OS/driver) — it limits dynamics. Disable mic 'enhancements' in your system sound settings for full-range recordings.", 9000);
  }
}

// A typical built-in-audio round-trip; calibration finds the exact value, but
// this gets a first-time user roughly on the beat without the extra step.
const DEFAULT_LATENCY_MS = 40;
function setRecOffset(ms: number) { recOffsetMs = ms; recOffsetIn.value = String(ms); localStorage.setItem("webcut.recOffsetMs", String(ms)); }
// "Configured" = the user has calibrated, typed an offset, or seen+dismissed
// the suggestion. We only nudge a brand-new user who hasn't done any of these.
function latencyConfigured(): boolean { return localStorage.getItem("webcut.recOffsetMs") !== null || localStorage.getItem("webcut.latencyAcked") === "1"; }
function ackLatency() { localStorage.setItem("webcut.latencyAcked", "1"); }

/** First-time nudge: suggest calibrating (or a sensible default) before the
 *  very first recording, so takes land on the beat instead of drifting late. */
function suggestLatencyCalibration() {
  const body = el("div", {}, [
    el("p", { text: "Recording latency isn't set yet. Your mic/interface adds a round-trip delay, so takes recorded against existing tracks can land slightly late unless it's compensated." }),
    el("p", { class: "hint", text: `Calibrate measures it exactly (plays clicks through your speakers and records them back). Or apply a typical ~${DEFAULT_LATENCY_MS} ms default now and fine-tune later with the “Latency ms” box.` }),
  ]);
  openModal("Set recording latency first", body, [
    { label: "⇄ Calibrate now", primary: true, onClick: () => { ackLatency(); runCalibration(); } },
    { label: `Use ~${DEFAULT_LATENCY_MS} ms default`, onClick: () => { setRecOffset(DEFAULT_LATENCY_MS); ackLatency(); toast(`Latency offset set to ${DEFAULT_LATENCY_MS} ms — calibrate later for an exact value.`, 5000); beginRecording(); } },
    { label: "Record without it", onClick: () => { ackLatency(); beginRecording(); } },
  ]);
}

// ---- camera (video) recording ---------------------------------------
let camRec: { recorder: MediaRecorder; chunks: Blob[]; stream: MediaStream; trackId: string | null; startSec: number } | null = null;

// On-screen recording indicator: a floating banner with a live elapsed clock.
const recClock = el("span", { class: "rec-clock", text: "0:00" });
const recBanner = el("div", { class: "rec-banner" }, [
  el("span", { class: "rec-dot" }),
  el("b", { text: "Recording" }),
  recClock,
  el("span", { class: "rec-stop", text: "· click 🎥 or press Esc to stop" }),
]);
recBanner.style.display = "none";
document.body.append(recBanner);
let recBannerTimer: any = null;
const fmtClock = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
function showRecBanner() {
  const startedAt = performance.now();
  recClock.textContent = "0:00";
  recBanner.style.display = "flex";
  clearInterval(recBannerTimer);
  recBannerTimer = setInterval(() => { recClock.textContent = fmtClock((performance.now() - startedAt) / 1000); }, 250);
}
function hideRecBanner() { clearInterval(recBannerTimer); recBannerTimer = null; recBanner.style.display = "none"; }

async function toggleVideoRecord() {
  if (camRec) { camRec.recorder.stop(); return; } // stop → import on 'stop'
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
    toast("Camera recording needs a secure context (https or localhost).", 7000); return;
  }
  try {
    // Camera + mic are only accessed here, at record time. Request the configured
    // resolution (the browser picks the closest mode the camera supports).
    const [qw, qh] = REC_QUALITIES[recQuality] ?? [1920, 1080];
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: qw }, height: { ideal: qh } },
      audio: micAudioConstraints(),
    });
    const mime = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"]
      .find((m) => MediaRecorder.isTypeSupported(m)) || "video/webm";
    // Record onto the armed video track if there is one; otherwise just to Media.
    const armedVideo = store.project.tracks.find((t) => t.kind === "video" && t.armed);
    const trackId = armedVideo?.id ?? null;
    const startSec = engine.position();

    const recorder = new MediaRecorder(stream, { mimeType: mime });
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    recorder.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      tCam.classList.remove("on");
      hideRecBanner();
      timeline.setVideoRecordOverlay(null);
      const tid = camRec?.trackId ?? null, at = camRec?.startSec ?? 0;
      camRec = null;
      const type = (recorder.mimeType || mime).split(";")[0] || "video/webm";
      const file = new File([new Blob(chunks, { type })], `Camera ${new Date().toLocaleTimeString().replace(/[: ]/g, "-")}.webm`, { type });
      const hadVideo = stream.getVideoTracks().length > 0;
      const asset = await importVideoToMedia(file);
      if (!asset) return;
      if (tid && store.track(tid)) placeAssetOnTimeline(asset.id, at, tid);
      if (asset.kind !== "video") toast(hadVideo
        ? "The camera recording's picture couldn't be read back, so only its sound was kept. Try a lower camera quality in ⚙ Recording."
        : "No camera picture was captured — only sound was recorded. Check that the camera is connected and not in use by another app.", 10000);
      else toast(tid && store.track(tid) ? "Camera clip recorded to the timeline." : "Camera clip added to Media (no video track armed).");
    };
    recorder.start();
    camRec = { recorder, chunks, stream, trackId, startSec };
    tCam.classList.add("on");
    showRecBanner();
    if (trackId) timeline.setVideoRecordOverlay({ trackId, startSec, since: performance.now() });
  } catch (e: any) {
    console.error(e); camRec = null; tCam.classList.remove("on"); hideRecBanner(); timeline.setVideoRecordOverlay(null);
    toast(e?.name === "NotAllowedError" ? "Camera/mic access blocked. Allow it in your browser's site settings." : "Could not access the camera.", 7000);
  }
}

async function toggleRecord() {
  if (engine.recording) { await finishRecord(); return; }
  // An armed video track means "record from the camera" (video + its sound).
  if (camRec || store.project.tracks.some((t) => t.kind === "video" && t.armed)) { await toggleVideoRecord(); return; }
  if (armedTracks().length === 0) { toast("Arm a track first — click the red ● on a track header (video track = camera, audio track = mic)."); return; }
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
    toast("Recording needs a secure context. Open the app via http://localhost:5175 (not a LAN IP) or use https.", 8000);
    return;
  }
  await beginRecording();
}

async function beginRecording() {
  const armed = armedTracks();
  if (armed.length === 0) return;
  await engine.resume();
  // Always (re)arm fresh right before recording so we have a live mic stream and
  // surface a clear error if the mic can't be opened — instead of failing silently.
  try {
    await recorder.arm(armed);
  } catch (e) {
    console.error("record arm failed:", e);
    toast(micError(e), 7000);
    return;
  }
  if (recorder.count() === 0) { toast("No usable microphone input.", 6000); return; }
  warnIfInputProcessed();
  const from = engine.position();
  engine.recording = true;
  tRec.classList.add("on");
  // Wait until playback is really scheduled: play() is async, and starting the
  // capture before it put every take ~60 ms late (the transport's start lead).
  await engine.play(from);
  recordAnchor = engine.anchor();
  recorder.begin();
  // Timeline position at the instant capture starts (refined at finish from the
  // capture's own first-sample time); latency is compensated at finish.
  recordStartPos = engine.position();
  // Recording the partner's mic: let them know (they see a banner while it runs),
  // and start measuring the network delay their audio will arrive with.
  partnerTrackIds = new Set(armed.filter((a) => a.deviceId === REMOTE_INPUT).map((a) => a.id));
  recordingPartner = partnerTrackIds.size > 0;
  partnerStatsStart = null;
  if (recordingPartner) {
    remote.notifyRecordingPeer(true);
    remote.requestLatency();
    void remote.delayStats().then((s) => { partnerStatsStart = s; });
    // Their app records its mic too (exact timing, full quality) and sends it at stop.
    const anchor = recordAnchor;
    partnerTake = anchor ? { takeId: uid("ptk"), anchor } : null;
    if (partnerTake) remote.startPartnerTake(partnerTake.takeId);
  }
  toast(recordingPartner ? "● Recording — including your partner's mic (they can see this)." : "● Recording…", 2500);
}
let recordingPartner = false;
/** Where the timeline was on the audio clock when this recording started. */
let recordAnchor: { pos: number; at: number } | null = null;
let partnerTrackIds = new Set<string>();
let partnerStatsStart: Awaited<ReturnType<typeof remote.delayStats>> = null;
let lastPartnerComp: PartnerComp | null = null;
/** The take being recorded from the partner: id + where our timeline was on our audio clock. */
let partnerTake: { takeId: string; anchor: { pos: number; at: number } } | null = null;
/** Network takes waiting for the partner's full-quality version. */
const pendingPartnerTakes = new Map<string, { anchor: { pos: number; at: number }; clips: { clipId: string; placed: { start: number; offset: number; duration: number } }[] }>();

/** Fine-tune for partner recordings (ms, + = slide earlier), kept per browser. */
let partnerOffsetMs = Number(localStorage.getItem("webcut.partnerOffsetMs") || 0);
// Used only when the audio path couldn't be measured with a probe:
const OPUS_DELAY = 0.03;     // Opus 20 ms frame + lookahead + packetizing (typical)
const BROWSER_BUFFER = 0.09; // browser audio buffering in and out of the call (not in WebRTC stats)
interface PartnerComp { total: number; transport: number; partner: number; path: number; manual: number; pathMeasured: boolean; rtt: number; jitter: number; }

/** How late the partner's voice lands in our recording, in seconds:
 *    transport — their playback runs behind ours (≈0 once the clocks are synced,
 *                since Play/Record starts them in step; else ½ RTT)
 *  + partner   — their own audio round trip: hearing the playback + their mic
 *                (their calibration, reported by their app)
 *  + path      — their audio → network → jitter buffer → our recorder: measured
 *                with a probe chirp and adjusted for the jitter buffer during the
 *                take; estimated from WebRTC stats when no probe is available
 *  + manual    — the fine-tune in ⇄ Remote. */
function partnerCompensation(end: Awaited<ReturnType<typeof remote.delayStats>>, start = partnerStatsStart): PartnerComp {
  const rtt = end?.rtt ?? start?.rtt ?? 0.1;
  const jitter = jbBetween(start, end) ?? 0.04;
  const transport = remote.clockOffset != null ? 0 : rtt / 2;
  const partner = remote.partnerLatency ?? 0.05; // they didn't report → a typical device
  const pathMeasured = remote.pipelineDelay != null;
  const path = pathMeasured
    ? remote.pipelineDelay! + (jitter - (remote.pipelineJb ?? jitter)) // measured, tracking jitter-buffer changes
    : rtt / 2 + jitter + OPUS_DELAY + BROWSER_BUFFER;
  const manual = partnerOffsetMs / 1000;
  const total = Math.max(0, transport + partner + path + manual);
  return { total, transport, partner, path, manual, pathMeasured, rtt, jitter };
}
const compText = (c: PartnerComp) =>
  `${c.transport ? `playback sync ${ms(c.transport)} + ` : ""}their audio ${ms(c.partner)}${remote.partnerLatency == null ? " (estimated)" : ""} + audio path ${ms(c.path)} (${c.pathMeasured ? "measured" : "estimated"})${c.manual ? ` + fine-tune ${ms(c.manual)}` : ""}`;
const ms = (s: number) => `${Math.round(s * 1000)} ms`;

/** Round-trip latency to shift recorded takes earlier so they sit on the beat:
 *  output latency + (best-effort) input latency + the user's manual offset. */
function recordCompensation(): number {
  const out = engine.ctx.outputLatency || engine.ctx.baseLatency || 0;
  return out + recorder.inputLatency() + recOffsetMs / 1000;
}

async function finishRecord() {
  engine.recording = false;
  tRec.classList.remove("on");
  const partnerTracks = partnerTrackIds;
  let pComp: PartnerComp | null = null;
  if (recordingPartner) {
    recordingPartner = false; remote.notifyRecordingPeer(false);
    pComp = partnerCompensation(await remote.delayStats());
    lastPartnerComp = pComp;
  }
  partnerTrackIds = new Set();
  const take = partnerTake; partnerTake = null;
  // Exact placement: the capture's first sample (audio clock) → timeline.
  const first = recorder.firstSampleTime();
  if (first != null && recordAnchor) recordStartPos = recordAnchor.pos + (first - recordAnchor.at);
  recordAnchor = null;
  const captured = await recorder.finish();
  engine.pause();
  const mode = store.project.recordMode ?? "comp"; // project-wide record mode
  const localComp = recordCompensation(); // shift take earlier to align with playback
  for (const [trackId, buf] of captured) {
    // Slide the take earlier by the latency — our own mic's, or for a take of
    // the partner's mic, the network + their device delay. If it would cross 0,
    // trim the head into the clip offset instead so nothing is lost.
    const comp = partnerTracks.has(trackId) && pComp ? pComp.total : localComp;
    let start = recordStartPos - comp, offset = 0;
    if (start < 0) { offset = Math.min(buf.duration, -start); start = 0; }
    const recEnd = start + (buf.duration - offset);
    // Overwrite punches over existing audio in the recorded region first.
    if (mode === "overwrite") store.overwriteRegion(trackId, start, recEnd);
    const asset = store.addAsset(buf, `Take ${new Date().toLocaleTimeString()}`);
    const clip = newClip(asset.id, asset, start);
    clip.offset = offset;
    clip.duration = buf.duration - offset;
    store.addClip(trackId, clip);
    // Comping stacks overlapping takes; Sound on Sound just layers (no change).
    if (mode === "comp") store.groupAsTake(trackId, clip.id);
    if (take && partnerTracks.has(trackId)) {
      const p = pendingPartnerTakes.get(take.takeId) ?? { anchor: take.anchor, clips: [] };
      p.clips.push({ clipId: clip.id, placed: { start: clip.start, offset: clip.offset, duration: clip.duration } });
      pendingPartnerTakes.set(take.takeId, p);
    }
  }
  if (take) remote.stopPartnerTake(take.takeId); // their app now sends its full-quality take
  recorder.disposeAll();
  lastArmSig = "";
  if (captured.size && pComp) toast(`Recorded ${captured.size} take(s). Partner take moved ${ms(pComp.total)} earlier to line up: ${compText(pComp)}.`, 10000);
  else if (captured.size) toast(`Recorded ${captured.size} take(s).`);
  else toast("No audio captured — check the mic level meter on the armed track.", 6000);
}

// ---- files -----------------------------------------------------------
// Native disk project files (.webcut — full video project incl. media). File
// System Access API where available (Save writes back to the same file);
// download/upload fallback elsewhere. (.dawproject stays an audio interchange
// option in the Import/Export menus.)
let fileHandle: any = null; // FileSystemFileHandle
const FSA = "showSaveFilePicker" in window;
const WEBCUT_TYPES = [{ description: "WebCut project", accept: { "application/zip": [".webcut"] } }];
const projFilename = () => sanitize(store.project.name) + ".webcut";

function setNameFromFile(fname: string) {
  const base = fname.replace(/\.(webcut|dawproject)$/i, "");
  if (base) { store.project.name = base; projName.value = base; store.emitChange("project"); }
}

// ---- unsaved-changes tracking ------------------------------------------
// "Dirty" = the project differs from what was last saved (or opened). Compared
// by content, so undoing back to the saved state counts as clean again.
const contentSig = () => JSON.stringify({ ...store.project, modifiedAt: 0, formatVersion: 0, savedWith: "" });
let savedSig = contentSig();
let maybeDirty = false;
let dirtyNow = false;
let exporting = false;   // an export is writing — leaving would lose it
let leavingOk = false;   // set right before an intentional reload (New project)
function isDirty(): boolean {
  if (!maybeDirty) return false;
  dirtyNow = contentSig() !== savedSig;
  if (!dirtyNow) maybeDirty = false;
  return dirtyNow;
}
function markSaved() { savedSig = contentSig(); maybeDirty = false; dirtyNow = false; updateTitle(); }
function updateTitle() { document.title = `${dirtyNow ? "● " : ""}${store.project.name || "Untitled"} — WebCut`; }
let titleTimer: any = null;
store.on("change", ({ scope }) => {
  if (scope === "selection") return;
  maybeDirty = true;
  clearTimeout(titleTimer);
  titleTimer = setTimeout(() => { isDirty(); updateTitle(); }, 400); // debounced: compares the whole project
});
updateTitle();
// Leaving the page (close tab, reload, navigate away) with unsaved work → the
// browser's own "Leave site?" dialog (browsers don't allow custom text here).
window.addEventListener("beforeunload", (e) => {
  if (leavingOk) return;
  if (exporting || isDirty()) { e.preventDefault(); e.returnValue = ""; }
});

/** Before replacing the current project: if it has unsaved changes, ask to
 *  Save / Don't save / Cancel. Resolves true to go ahead. */
function confirmDiscard(what: string): Promise<boolean> {
  if (!isDirty()) return Promise.resolve(true);
  return new Promise((resolve) => {
    let settled = false;
    const done = (v: boolean) => { if (!settled) { settled = true; resolve(v); } };
    const name = store.project.name || "Untitled";
    const body = el("div", {}, [
      el("p", { text: `“${name}” has changes that haven't been saved. ${what} will replace it.` }),
      el("p", { class: "hint", text: fileHandle ? `Save writes them to ${fileHandle.name}.` : "Save asks where to keep the project file." }),
    ]);
    const close = openModal("Save changes first?", body, [
      { label: "Cancel", onClick: () => done(false) },
      { label: "Don't save", onClick: () => done(true) },
      { label: "Save", primary: true, onClick: () => { saveToFile(false).then((ok) => done(ok)); } },
    ]);
    void close;
    onBackdropCancel(() => done(false)); // clicking outside the dialog = cancel
  });
}
/** Run `fn` if the newest dialog is dismissed by clicking its backdrop. */
function onBackdropCancel(fn: () => void) {
  const all = document.querySelectorAll(".modal-bg");
  const bg = all[all.length - 1];
  bg?.addEventListener("mousedown", (e) => { if (e.target === bg) fn(); });
}

/** A yes/no confirmation; resolves true for the primary choice. */
function confirmChoice(title: string, text: string, yes: string, no = "Cancel", extra?: { label: string; onClick: () => void }): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (v: boolean) => { if (!settled) { settled = true; resolve(v); } };
    openModal(title, el("p", { text }), [
      { label: no, onClick: () => done(false) },
      ...(extra ? [{ label: extra.label, onClick: () => { done(false); extra.onClick(); } }] : []),
      { label: yes, primary: true, onClick: () => done(true) },
    ]);
    onBackdropCancel(() => done(false));
  });
}

// What we know about the current project file, to warn before overwriting it.
let fileOrigin: "opened" | "saved" | null = null; // how we got the handle
let fileOverwriteOk = false;                       // user already OK'd saving over the opened file
let fileLastModified = 0;                          // the file's timestamp when we last read/wrote it

/** If the file the user picked in Save As already holds a *different* WebCut
 *  project (or something that isn't one), confirm before replacing it. The OS
 *  dialog already asked about overwriting; this adds what's inside. */
async function confirmReplaceExisting(handle: any): Promise<boolean> {
  let f: File;
  try { f = await handle.getFile(); } catch { return true; }
  if (!f.size) return true; // new (empty) file
  let otherName: string | null = null, isProject = false;
  try {
    const entries = await readZip(f);
    const pj = entries.get("project.json");
    if (pj) {
      isProject = true;
      const p = JSON.parse(new TextDecoder().decode(await zipEntryBytes(f, pj)));
      if (p?.id === store.project.id) return true; // an older save of this same project
      otherName = typeof p?.name === "string" ? p.name : null;
    }
  } catch { /* not a zip */ }
  return confirmChoice("Replace existing file?",
    isProject
      ? `${handle.name} contains a different project${otherName ? ` (“${otherName}”)` : ""}. Saving will replace it, and that project will be lost.`
      : `${handle.name} already exists and isn't a WebCut project. Saving will replace it.`,
    "Replace it");
}

/** Save the project to its .webcut file. Resolves true if it was saved. */
async function saveToFile(forceNew = false): Promise<boolean> {
  try {
    // Pick the file first to keep the user-gesture activation, then pack (async
    // because media blobs must be read into the zip).
    if (FSA) {
      let handle = fileHandle;
      if (forceNew || !handle) {
        handle = await (window as any).showSaveFilePicker({ suggestedName: projFilename(), types: WEBCUT_TYPES });
        if (!(await confirmReplaceExisting(handle))) return false;
      } else {
        // Saving into the existing file: warn if it changed on disk since we last
        // read/wrote it, and the first time we'd overwrite a file the user opened.
        let current: File | null = null;
        try { current = await handle.getFile(); } catch { /* removed/moved — writing recreates it */ }
        if (current && fileLastModified && current.lastModified !== fileLastModified) {
          const ok = await confirmChoice("File changed on disk",
            `${handle.name} was modified after you ${fileOrigin === "opened" && !fileOverwriteOk ? "opened" : "last saved"} it (perhaps in another tab or app). Saving now will replace those changes.`,
            "Save over it", "Cancel", { label: "Save As…", onClick: () => { saveToFile(true); } });
          if (!ok) return false;
        } else if (fileOrigin === "opened" && !fileOverwriteOk) {
          const ok = await confirmChoice("Save over the opened file?",
            `This replaces ${handle.name} — the file you opened — with your edited project. Use Save As to keep the original.`,
            "Save over it", "Cancel", { label: "Save As…", onClick: () => { saveToFile(true); } });
          if (!ok) return false;
        }
      }
      const blob = await exportWebcut(store);
      const w = await handle.createWritable(); // the browser commits the file only on close()
      try { await w.write(blob); await w.close(); }
      catch (err) { try { await w.abort(); } catch {} throw err; } // never leave a half-written project
      // Read it back: the archive must be complete and contain the project.
      const saved = await handle.getFile();
      const entries = saved.size === blob.size ? await readZip(saved).catch(() => null) : null;
      if (!entries?.has("project.json")) throw new Error("The saved file didn't verify — try Save As to a different location.");
      if (handle !== fileHandle) { fileHandle = handle; fileOrigin = "saved"; setNameFromFile(handle.name); }
      fileOverwriteOk = true;
      fileLastModified = saved.lastModified;
      toast(`Saved ${handle.name} (${fmtBytes(saved.size)}).`);
    } else {
      const blob = await exportWebcut(store);
      downloadBlob(blob, projFilename()); // fallback: lands in Downloads
      toast("Saved .webcut to your downloads.");
    }
    // The project now has a real file — drop the browser quick-save and stop auto-saving.
    onProjectFileBacked();
    markSaved();
    return true;
  } catch (e: any) {
    if (e?.name === "AbortError") return false; // user canceled the picker
    console.error(e); toast(`Save failed${e?.message ? ` — ${e.message}` : "."}`, 9000);
    return false;
  }
}

/** Open a .webcut (any version) and tell the user about anything noteworthy. */
async function openWebcutFile(file: Blob, name: string) {
  toast(`Opening ${name}…`, 4000);
  const r: WebcutLoadReport = await importWebcut(file, store, engine.ctx);
  setNameFromFile(name);
  const notes: string[] = [];
  if (r.newer) notes.push("it was saved by a newer WebCut — features this version doesn't know are ignored");
  if (r.missing.length) notes.push(`${r.missing.length} media file${r.missing.length > 1 ? "s" : ""} couldn't be loaded (${r.missing.slice(0, 3).join(", ")}${r.missing.length > 3 ? "…" : ""})`);
  toast(notes.length ? `Opened ${name}, but ${notes.join("; ")}.` : `Opened ${name}.`, notes.length ? 12000 : 3000);
}

// ---- browser quick-save: auto every 10 min until the project has a real file ----
let fileBacked = false;
function onProjectFileBacked() { fileBacked = true; deleteProject(store.project.id).catch(() => {}); }
function projectHasContent() { return store.project.assets.length > 0 || store.project.tracks.some((t) => t.clips.length > 0); }
const QUICK_SAVE_MS = 10 * 60 * 1000;
setInterval(() => {
  if (fileBacked || !projectHasContent()) return;
  saveProject(store).catch((e) => console.warn("auto quick-save failed", e));
}, QUICK_SAVE_MS);

/** Opened a project from a .webcut file (or a browser save): it's the new
 *  "saved" baseline, and future Saves go to `handle` (null = no file yet). */
function adoptOpenedProject(handle: any, lastModified = 0) {
  fileHandle = handle;
  fileOrigin = handle ? "opened" : null;
  fileOverwriteOk = false;
  fileLastModified = lastModified;
  markSaved();
}

async function openFromFile() {
  try {
    if (FSA) {
      // Pick first (needs the click), then offer to save the current project.
      const [h] = await (window as any).showOpenFilePicker({ types: WEBCUT_TYPES, multiple: false });
      if (!(await confirmDiscard(`Opening ${h.name}`))) return;
      const file = await h.getFile();
      await openWebcutFile(file, h.name);
      adoptOpenedProject(h, file.lastModified); // only once it loaded — a failed open must not become the Save target
      fileBacked = true; // opened a real .webcut file → no browser quick-save needed
    } else {
      pickFile(".webcut,application/zip", async (file) => {
        try {
          if (!(await confirmDiscard(`Opening ${file.name}`))) return;
          await openWebcutFile(file, file.name);
          adoptOpenedProject(null);
          fileBacked = true;
        } catch (e: any) { console.error(e); toast(`Could not open that .webcut file${e?.message ? ` — ${e.message}` : "."}`, 9000); }
      });
    }
  } catch (e: any) {
    if (e?.name === "AbortError") return;
    console.error(e); toast(`Could not open that file${e?.message ? ` — ${e.message}` : "."}`, 9000);
  }
}

async function newProject() {
  if (!(await confirmDiscard("A new project"))) return;
  leavingOk = true; // already confirmed — don't show the browser's leave warning too
  location.reload();
}

function openFileMenu() {
  const body = el("div", { class: "list" });
  body.append(
    menuItem("🆕 New project", newProject),
    menuItem("📂 Open project file…", openFromFile),
    menuItem("💾 Save" + (FSA && fileHandle ? ` (${fileHandle.name})` : " project file…"), () => saveToFile(false)),
    menuItem("💾 Save As…", () => saveToFile(true)),
  );
  body.append(el("hr"));
  body.append(
    menuItem("☁️ Quick-save to browser", async () => {
      try { await saveProject(store); if (!fileHandle) markSaved(); toast(fileHandle ? `Saved to this browser (${fileHandle.name} itself isn't updated — use Save for that).` : "Saved to this browser.", 5000); }
      catch (e: any) { console.error(e); toast(`Browser save failed${e?.message ? ` — ${e.message}` : "."}`, 8000); }
    }),
    menuItem("🗂️ Open from browser…", openProjects),
  );
  if (!FSA) body.append(el("p", { class: "hint", text: "Your browser saves project files to Downloads and opens them via a file picker. (Chrome/Edge can save in place.)" }));
  openModal("File", body);
}

async function openProjects() {
  const items = await listProjects();
  const body = el("div", { class: "list" });
  if (!items.length) body.append(el("p", { class: "hint", text: "No saved projects yet." }));
  for (const p of items) {
    const item = el("div", { class: "item" }, [
      el("div", { class: "meta", html: `<b>${p.name}</b><br><small>${p.trackCount} tracks · ${new Date(p.modifiedAt).toLocaleString()}</small>` }),
    ]);
    const open = btn("Open", async () => {
      document.querySelector(".modal-bg")?.remove();
      if (!(await confirmDiscard(`Opening “${p.name}”`))) return;
      if (!(await loadProject(p.id, store, engine.ctx))) { toast("That browser save couldn't be loaded."); return; }
      projName.value = store.project.name; fileBacked = false;
      adoptOpenedProject(null);
      toast("Loaded.");
    });
    const del = btn("Delete", async () => { await deleteProject(p.id); item.remove(); });
    item.append(open, del);
    body.append(item);
  }
  openModal("Open project", body);
}

function openImportMenu() {
  const body = el("div", { class: "list" });
  body.append(
    menuItem("🎬 Import video → Media (MP4/WebM/MOV/…)", () => pickFile("video/*", (f) => importVideoToMedia(f))),
    menuItem("🎵 Import audio → Media (WAV/MP3/…)", () => pickFile("audio/*", (f) => importAudioToMedia(f))),
    menuItem("📦 Import .dawproject", () => pickFile(".dawproject,application/zip", importDaw)),
  );
  body.append(el("hr"), el("p", { class: "hint", text: "Or create media:" }));
  body.append(
    menuItem("■ Color matte / black", () => newGeneratedItem("color")),
    menuItem("▥ Bars & tone", () => newGeneratedItem("bars")),
    menuItem("T Title / lower third", () => newGeneratedItem("title")),
    menuItem("⏲ Countdown leader", () => newGeneratedItem("countdown")),
  );
  body.append(el("p", { class: "hint", text: "Imports land in the Media panel. Double-click or drag an item onto the timeline to use it." }));
  openModal("Import", body);
}

function openExportMenu() {
  const body = el("div", { class: "list" });
  body.append(
    menuItem(`🎬 MP4 video — H.264 + AAC…${videoExportSupported() ? "" : " (needs Chrome/Edge)"}`, openVideoExportDialog),
    menuItem(`🎧 M4A audio — AAC 192 kbps${aacSupported() ? "" : " (needs Chrome/Edge)"}`, () => exportAudioMix("m4a")),
    menuItem("🌊 WAV audio — 24-bit 48 kHz mixdown", () => exportAudioMix("wav")),
    menuItem("🎚️ Session stems — one 24-bit WAV per track (.zip)", doExportStems),
    menuItem("📦 .dawproject — audio tracks for a DAW", () => runExport({
      title: "Export .dawproject", filename: sanitize(store.project.name) + ".dawproject", mime: "application/zip", ext: ".dawproject", description: "DAWproject",
      run: async () => exportDawproject(store),
    })),
  );
  body.append(el("p", { class: "hint", text: "MP4s use the most widely supported settings (H.264 High, AAC-LC, BT.709, fast start) so they play everywhere and import into Premiere, Resolve, Final Cut and other editors." }));
  openModal("Export", body);
}

/** Run an export with a progress dialog. Asks where to save first when the
 *  browser supports it (the file is then written directly, any size), else
 *  downloads the result. Must be called from a click. */
async function runExport(o: {
  title: string; filename: string; mime: string; ext: string; description: string;
  run: (sink: ExportSink, signal: AbortSignal, progress: (text: string, frac: number) => void) => Promise<Blob | null>;
}) {
  let handle: any = null;
  try { handle = await pickSaveFile(o.filename, o.description, o.mime, o.ext); }
  catch (e: any) { if (e?.name === "AbortError") return; handle = null; }
  if (engine.playing || engine.recording) engine.stop();
  const ac = new AbortController();
  const status = el("p", { class: "hint", text: "Preparing…" });
  const bar = el("div", { class: "progbar" }, [el("i")]);
  const fill = bar.firstChild as HTMLElement;
  const body = el("div", {}, [status, bar, el("p", { class: "hint", text: handle ? `Writing to ${handle.name}. Keep this tab open.` : "Keep this tab open; the file downloads when done." })]);
  const close = openModal(o.title, body, [{ label: "Cancel", onClick: () => { ac.abort(); return false; } }]);
  const t0 = performance.now();
  let sink: ExportSink | null = null;
  exporting = true; // leaving the page now would lose the export
  try {
    sink = handle ? await FileSink.open(handle) : new BlobSink(o.mime);
    const result = await o.run(sink, ac.signal, (text, frac) => {
      const pct = Math.round(frac * 100);
      const secs = (performance.now() - t0) / 1000;
      const eta = frac > 0.03 ? ` · ~${Math.max(1, Math.round(secs / frac - secs))} s left` : "";
      status.textContent = `${text} ${pct}%${eta}`;
      fill.style.width = `${pct}%`;
    });
    // run() returns either the sink's own Blob, null (already written to disk),
    // or a finished Blob it built itself (ZIP exports) that still has to be stored.
    let size = result?.size ?? 0;
    if (sink instanceof FileSink) {
      if (result) await sink.writeBlob(result);
      await sink.close();
      size = (await handle.getFile()).size;
    } else if (result) downloadBlob(result, o.filename);
    close();
    toast(`Exported ${handle?.name ?? o.filename} (${fmtBytes(size)}).`, 6000);
  } catch (e: any) {
    await sink?.abort();
    close();
    if (e?.name === "AbortError") { toast("Export cancelled — nothing was written."); return; }
    console.error(e);
    toast(e?.message || "Export failed.", 9000);
  } finally {
    exporting = false;
  }
}
const fmtBytes = (n: number) => n >= 1e9 ? `${(n / 1e9).toFixed(2)} GB` : n >= 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`;

/** The range an export covers: In→Out when both marks are set and chosen, else everything. */
function exportRange(useInOut: boolean, tail = 0) {
  const { markIn, markOut } = store.project;
  if (useInOut && markIn != null && markOut != null && markOut > markIn) return { start: markIn, end: markOut };
  return { start: 0, end: contentDuration(store.project) + tail };
}

function openVideoExportDialog() {
  if (!videoExportSupported()) { toast("Video export needs WebCodecs — use Chrome or Edge desktop.", 7000); return; }
  const p = store.project;
  if (!(contentDuration(p) > 0)) { toast("Nothing to export — add a clip to the timeline first."); return; }
  const sel = (opts: [string, string][], value: string) => {
    const s = el("select") as HTMLSelectElement;
    for (const [v, l] of opts) s.append(el("option", { text: l, attrs: { value: v } }));
    s.value = value; return s;
  };
  const sizes = exportSizes(p);
  const sizeSel = sel(sizes.map((z, i) => [String(i), z.label]), "0");
  const rate = frameRateFor(p.fps || 30);
  const rateSel = sel(FRAME_RATES.map((r) => [r.label, `${r.label} fps${r.label === rate.label ? " (project)" : ""}`]), rate.label);
  const qualSel = sel([["high", "High — for editing / archiving"], ["medium", "Medium — sharing & upload"], ["small", "Small file"]], "high");
  const hasInOut = p.markIn != null && p.markOut != null && p.markOut > p.markIn;
  const rangeSel = sel([["all", "Whole timeline"], ...(hasInOut ? [["inout", `In → Out (${fmtTimecode(p.markOut! - p.markIn!, p.fps)})`] as [string, string]] : [])], "all");
  const info = el("p", { class: "hint" });
  const upd = () => {
    const z = sizes[Number(sizeSel.value)], r = FRAME_RATES.find((x) => x.label === rateSel.value)!;
    const { start, end } = exportRange(rangeSel.value === "inout");
    const bpp = { high: 0.12, medium: 0.075, small: 0.045 }[qualSel.value as "high"];
    const vbr = Math.max(1.5e6, z.w * z.h * (r.num / r.den) * bpp);
    info.textContent = `H.264 High profile · ${(vbr / 1e6).toFixed(1)} Mbps · AAC-LC 48 kHz stereo 192 kbps · ${fmtTimecode(end - start, p.fps)} · ≈ ${fmtBytes(((vbr + 192e3) / 8) * (end - start))}`;
  };
  for (const s of [sizeSel, rateSel, qualSel, rangeSel]) s.addEventListener("change", upd);
  upd();
  const row = (label: string, ctl: HTMLElement) => el("div", { class: "fx-param exp-row" }, [el("label", { text: label }), ctl]);
  const body = el("div", { class: "fx-editor" }, [
    row("Size", sizeSel), row("Frame rate", rateSel), row("Quality", qualSel), row("Range", rangeSel), info,
    el("p", { class: "hint", text: "Standard MP4 (moov first, constant frame rate, 4:2:0 BT.709, keyframe every 2 s) — plays in QuickTime, Windows, VLC, phones and browsers, and imports cleanly into NLEs. A different frame rate than the project resamples the timeline (no speed change)." }),
  ]);
  openModal("Export MP4", body, [
    { label: "Cancel", onClick: () => {} },
    { label: "Export…", primary: true, onClick: () => {
      const z = sizes[Number(sizeSel.value)], r = FRAME_RATES.find((x) => x.label === rateSel.value)!;
      const range = exportRange(rangeSel.value === "inout");
      runExport({
        title: "Export MP4", filename: sanitize(p.name) + ".mp4", mime: "video/mp4", ext: ".mp4", description: "MP4 video",
        run: (sink, signal, progress) => exportVideo(store, {
          sink, signal,
          settings: { width: z.w, height: z.h, rate: r, quality: qualSel.value as ExportQuality, ...range },
          onProgress: (phase, frac) => progress(phase === "finalize" ? "Finalizing…" : "Rendering frames…", frac),
        }),
      });
    } },
  ]);
}

/** WAV / M4A mixdown of the whole timeline (+2 s so reverbs ring out). */
function exportAudioMix(kind: "wav" | "m4a") {
  const range = exportRange(false, 2);
  if (!(range.end > 2)) { toast("Nothing to export — add a clip to the timeline first."); return; }
  const name = sanitize(store.project.name);
  runExport(kind === "wav"
    ? { title: "Export WAV", filename: name + ".wav", mime: "audio/wav", ext: ".wav", description: "WAV audio",
        run: async (sink, signal, progress) => { await exportWavMix(store.project, store.buffers, { ...range, sink, signal, onProgress: (f) => progress("Rendering audio…", f) }); return sink.close(); } }
    : { title: "Export M4A", filename: name + ".m4a", mime: "audio/mp4", ext: ".m4a", description: "M4A audio",
        run: async (sink, signal, progress) => { await exportM4a(store.project, store.buffers, { ...range, sink, signal, onProgress: (f) => progress("Encoding audio…", f) }); return sink.close(); } });
}

function doExportStems() {
  runExport({
    title: "Export stems", filename: sanitize(store.project.name) + "_stems.zip", mime: "application/zip", ext: ".zip", description: "ZIP archive",
    run: async (_sink, _signal, progress) => exportStems(store.project, store.buffers, 48000, (f) => progress("Rendering stems…", f)),
  });
}

function menuItem(label: string, on: () => void) {
  const b = el("button", { text: label });
  b.style.textAlign = "left";
  b.addEventListener("click", () => { document.querySelector(".modal-bg")?.remove(); on(); });
  return b;
}

function pickFile(accept: string, cb: (file: File) => void) {
  const inp = el("input", { attrs: { type: "file", accept } }) as HTMLInputElement;
  inp.addEventListener("change", () => { if (inp.files?.[0]) cb(inp.files[0]); });
  inp.click();
}

/** Pick (or create) a track of the given kind: the selected track if it matches,
 *  else the first of that kind, else a newly created one. */
function targetTrack(kind: "video" | "audio"): string {
  const sel = store.selectedTrackId ? store.track(store.selectedTrackId) : null;
  if (sel?.kind === kind) return sel.id;
  const existing = store.project.tracks.find((t) => t.kind === kind);
  if (existing) return existing.id;
  return store.createTrack(kind).id;
}

/** Extract the file's audio as one buffer per audio track. Multi-track MP4/MOV
 *  files are demuxed (mp4box + WebCodecs) so each audio track gets its own lane;
 *  everything else falls back to a single decodeAudioData buffer. */
async function extractAudioTracks(file: File): Promise<{ buffers: AudioBuffer[]; reason: string }> {
  return extractAudioBuffers(file, engine.ctx);
}

/** Add a video file to the Media bin only (one item; its audio is held as hidden
 *  child assets). Does NOT place anything on the timeline. */
async function importVideoToMedia(file: File): Promise<MediaAsset | null> {
  toast(`Loading ${file.name}…`, 6000);
  let source: VideoSource | null = null;
  try { source = await VideoSource.load(file); }
  catch (e: any) { console.warn("[webcut] video load failed:", e); }

  // The browser couldn't decode the video (e.g. HEVC/ProRes/AVI). Rather than
  // failing the whole import, salvage the audio if the file has any (awaited, since
  // there's no video to show meanwhile).
  if (!source) {
    const { buffers: audios, reason } = await extractAudioTracks(file);
    if (audios.length) {
      const a = store.addAsset(audios[0], `${file.name} (audio)`);
      store.emitChange("assets");
      toast(`${file.name}: this video codec isn't supported by the browser — imported audio only. Convert to H.264 MP4 for video.`, 9000);
      return a;
    }
    toast(`Could not import ${file.name}: the browser can't decode this video${reason ? ` (${reason})` : ""}. Try converting to H.264 MP4.`, 10000);
    return null;
  }

  // Video is ready immediately — add it now and extract audio in the BACKGROUND so
  // large files never block the UI (audio extraction reads the whole file).
  const vAsset = store.addVideoAsset(source, file.name);
  vAsset.audioAssetIds = [];
  vAsset.hasAudio = false;
  vAsset.audioPending = true;
  store.emitChange("assets");
  toast(`Added ${file.name} — extracting audio in the background…`, 5000);

  extractAudioTracks(file).then(({ buffers: audios, reason }) => {
    if (!store.asset(vAsset.id)) return; // asset removed while we worked
    if (!(vAsset.duration > 0) && audios[0]) vAsset.duration = audios[0].duration;
    const childIds: string[] = [];
    audios.forEach((buf, i) => {
      const label = audios.length > 1 ? `${file.name} (audio ${i + 1})` : `${file.name} (audio)`;
      const a = store.addAsset(buf, label);
      a.ownerId = vAsset.id;
      childIds.push(a.id);
    });
    vAsset.audioAssetIds = childIds;
    vAsset.hasAudio = childIds.length > 0;
    vAsset.audioPending = false;
    vAsset.audioNote = childIds.length ? undefined : (reason || "no audio track found");
    // Attach audio to any clips of this video already placed on the timeline.
    if (childIds.length) attachAudioToPlacedClips(vAsset);
    store.emitChange("assets");
    toast(childIds.length
      ? `${file.name}: audio ready (${childIds.length} track${childIds.length > 1 ? "s" : ""}).`
      : `${file.name}: imported without audio${reason ? ` — ${reason}` : ""}.`, 9000);
  }).catch((e) => {
    console.warn("[webcut] background audio extract failed:", e);
    vAsset.audioPending = false;
    vAsset.audioNote = "audio extraction failed (file may be too large or the codec unsupported)";
    store.emitChange("assets");
    toast(`${file.name}: couldn't extract audio (file may be too large or the codec unsupported).`, 7000);
  });

  return vAsset;
}

/** When a video's audio finishes extracting after its clip is already on the
 *  timeline, drop the audio clip(s) onto parallel audio lanes, linked. */
function attachAudioToPlacedClips(vAsset: MediaAsset) {
  const childIds = vAsset.audioAssetIds ?? [];
  if (!childIds.length) return;
  for (const t of store.project.tracks) {
    if (t.kind !== "video") continue;
    for (const vClip of t.clips) {
      if (vClip.assetId !== vAsset.id || vClip.linkId) continue; // already linked or not ours
      const linkId = uid("lnk");
      vClip.linkId = linkId;
      childIds.forEach((aid, i) => {
        const aAsset = store.asset(aid);
        if (!aAsset) return;
        const trackId = i === 0 ? store.parallelTrack("audio", store.trackNumber(t)).id : store.createTrack("audio").id;
        const aClip = newClip(aid, aAsset, vClip.start);
        aClip.linkId = linkId;
        store.addClip(trackId, aClip);
      });
    }
  }
}

/** Add an audio file to the Media bin only. */
async function importAudioToMedia(file: File): Promise<MediaAsset | null> {
  try {
    const buf = await engine.ctx.decodeAudioData(await file.arrayBuffer());
    const asset = store.addAsset(buf, file.name);
    toast(`Added ${file.name} to Media.`);
    return asset;
  } catch { toast("Could not decode that audio file."); return null; }
}

/** Drop a media-bin asset onto the timeline. A video expands to its video clip
 *  plus each audio track on its own linked audio lane; an audio asset is one clip.
 *  When dropped on a specific track (Video N / Audio N), the video lands on Video N
 *  and its audio on the parallel Audio N — creating the next-numbered track if the
 *  parallel one doesn't exist. */
function placeAssetOnTimeline(assetId: string, atTime = engine.position(), dropTrackId?: string | null) {
  const asset = store.asset(assetId);
  if (!asset || asset.ownerId) return; // never place child audio assets on their own
  const dropTrack = dropTrackId ? store.track(dropTrackId) : null;
  const n = dropTrack ? store.trackNumber(dropTrack) : 0;
  // Titles overlay footage: unless dropped on a specific track, put them on a free
  // video track above whatever is visible there (creating one if needed).
  const g = asset.generator;
  const isOverlay = g?.type === "text" && g.bg !== "full";
  const placeDur = Math.min(asset.defaultDuration ?? asset.duration, asset.duration);
  const videoTrackId = () => dropTrack?.kind === "video" ? dropTrack.id : n ? store.parallelTrack("video", n).id
    : isOverlay ? overlayTrack(atTime, atTime + placeDur) : targetTrack("video");
  const audioTrackId = () => dropTrack?.kind === "audio" ? dropTrack.id : n ? store.parallelTrack("audio", n).id : targetTrack("audio");
  if (asset.kind === "video") {
    const childIds = asset.audioAssetIds ?? [];
    const linkId = childIds.length ? uid("lnk") : undefined;
    const vClip = newClip(asset.id, asset, atTime);
    if (linkId) vClip.linkId = linkId;
    store.addClip(videoTrackId(), vClip);
    childIds.forEach((aid, i) => {
      const aAsset = store.asset(aid);
      if (!aAsset) return;
      // First audio → the parallel/selected audio lane; extra tracks get their own.
      const trackId = i === 0 ? audioTrackId() : store.createTrack("audio").id;
      const aClip = newClip(aid, aAsset, atTime);
      aClip.linkId = linkId;
      store.addClip(trackId, aClip);
    });
  } else {
    store.addClip(audioTrackId(), newClip(asset.id, asset, atTime));
  }
}

/** A video track free over [t0,t1] that sits above every video track with a clip
 *  there (tracks are listed top-first), else a new top video track. */
function overlayTrack(t0: number, t1: number): string {
  const vids = store.project.tracks.filter((t) => t.kind === "video");
  const busy = (t: typeof vids[number]) => t.clips.some((c) => c.takeActive !== false && c.start < t1 && c.start + c.duration > t0);
  const topBusy = vids.findIndex(busy);
  if (topBusy < 0) return targetTrack("video");
  for (let i = topBusy - 1; i >= 0; i--) if (!busy(vids[i])) return vids[i].id; // nearest free track above
  return store.createTrack("video").id; // new tracks go on top
}

/** "＋ New item" → open the dialog for that kind; Add & place drops it at the playhead. */
function newGeneratedItem(kind: NewItemKind) {
  showSideTab("media");
  const textPreset = kind === "title" || kind === "lower" || kind === "subtitle" ? kind : undefined;
  openGenerateDialog(store, {
    type: textPreset ? "text" : (kind as Exclude<NewItemKind, "title" | "lower" | "subtitle">),
    textPreset,
    place: (id) => { placeAssetOnTimeline(id); toast("Placed at the playhead."); },
  });
}

async function importDaw(file: File) {
  // .dawproject import replaces the current project (it becomes a new, unsaved one).
  if (!(await confirmDiscard(`Importing ${file.name}`))) return;
  try {
    await importDawproject(await file.arrayBuffer(), store, engine.ctx);
    projName.value = store.project.name;
    fileHandle = null; fileOrigin = null; fileBacked = false;
    toast("Imported .dawproject");
  } catch (e) { console.error(e); toast("Failed to import .dawproject"); }
}

/** Import a file into the Media bin (video/audio) — or, for project files, open
 *  them. Returns the new media asset (or null for project files / failures). */
async function importFileToMedia(file: File): Promise<MediaAsset | null> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".webcut")) {
    try {
      if (!(await confirmDiscard(`Opening ${file.name}`))) return null;
      await openWebcutFile(file, file.name);
      // A dropped file has no writable handle: Save will ask where to save.
      adoptOpenedProject(null);
      fileBacked = true;
    } catch (e: any) { console.error(e); toast(`Could not open that .webcut file${e?.message ? ` — ${e.message}` : "."}`, 9000); }
    return null;
  }
  if (name.endsWith(".dawproject")) { await importDaw(file); return null; }
  if (file.type.startsWith("audio/") || /\.(wav|mp3|flac|ogg|oga|aac|m4a|aiff?|opus|weba)$/.test(name)) return importAudioToMedia(file);
  if (file.type.startsWith("video/") || /\.(mp4|webm|mov|mkv|m4v|avi|m2ts|mts|ts|wmv|flv|3gp|ogv|mpg|mpeg)$/.test(name)) return importVideoToMedia(file);
  // Unknown type but has a filename — try video (its importer salvages audio and
  // gives a specific reason if the browser can't decode it), rather than refuse outright.
  if (name) return importVideoToMedia(file);
  toast(`Unsupported file: ${file.name}`, 5000);
  return null;
}

/** Import each file to Media, then place media assets on the timeline. */
async function importFilesToTimeline(files: File[], atTime = engine.position(), dropTrackId?: string | null) {
  for (const f of files) {
    const asset = await importFileToMedia(f);
    if (asset) placeAssetOnTimeline(asset.id, atTime, dropTrackId);
  }
}

const ASSET_DND = "application/x-webcut-asset";

/** Accept dropped files (and, on the timeline, dragged media-bin assets). */
function enableDrop(target: HTMLElement, onFiles: (files: File[], e: DragEvent) => void, onAsset?: (assetId: string, e: DragEvent) => void) {
  target.addEventListener("dragover", (e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = "copy"; target.classList.add("drop-hover"); });
  target.addEventListener("dragleave", (e) => { if (e.target === target) target.classList.remove("drop-hover"); });
  target.addEventListener("drop", (e) => {
    e.preventDefault(); e.stopPropagation();
    target.classList.remove("drop-hover");
    const assetId = e.dataTransfer?.getData(ASSET_DND);
    if (assetId && onAsset) { onAsset(assetId, e); return; }
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length) onFiles(files, e);
  });
}

const sanitize = (s: string) => s.replace(/[^\w\-]+/g, "_") || "project";

// ---- effects picker --------------------------------------------------
function addFxPicker(target: string | "master") {
  const body = el("div", { class: "list" });
  for (const kind of ["eq", "compressor", "limiter", "lowpass", "highpass", "saturator", "chorus", "delay", "reverb", "width", "gain", "denoise", "deverb"] as const) {
    body.append(menuItem(EFFECT_LABELS[kind], () => {
      const fx = makeEffect(kind);
      store.addEffect(target, fx);
      openFxEditor(store, engine, target, fx.id, (id) => guiForWam(target, id)); // open its settings
    }));
  }
  body.append(el("hr"));
  body.append(menuItem("🔌 Load WAM plugin by URL…", () => loadWamPrompt(target)));
  openModal("Insert effect", body);
}

function loadWamPrompt(target: string | "master") {
  const body = el("div");
  const inp = el("input", { attrs: { placeholder: "https://…/index.js (WAM v2 module)" } }) as HTMLInputElement;
  inp.style.width = "100%";
  body.append(el("p", { class: "hint", text: "Paste the URL of a Web Audio Module (WAM v2) plugin." }), inp);
  openModal("Load WAM plugin", body, [
    { label: "Load", primary: true, onClick: () => { if (inp.value) loadWam(target, inp.value); } },
  ]);
}

async function loadWam(target: string | "master", url: string) {
  toast("Loading plugin…");
  try {
    const fx: Effect = await loadWamEffect(engine, target, url);
    store.addEffect(target, fx);
    toast("Plugin loaded.");
    openFxEditor(store, engine, target, fx.id, (id) => guiForWam(target, id)); // open its settings
  } catch (e: any) { console.error(e); toast(e?.message || "Failed to load plugin (needs a WAM-compatible URL)."); }
}

function guiForWam(target: string | "master", fxId: string) {
  const list = target === "master" ? store.project.masterFx : store.track(target)?.fx;
  const fx = list?.find((f) => f.id === fxId);
  if (fx) openWamGui(fx.id);
}

// ---- remote ----------------------------------------------------------
function openRemoteMenu() {
  const body = el("div");
  if (remote.active) {
    body.append(el("p", { class: "hint", html: `In room <b>${remote.room ?? ""}</b> — status: <b>${syncBadge.textContent}</b>.<br>Leaving stops sharing your mic and ends sync. Your project stays as-is.` }));
    // Recording: local only, unless the partner allows it for this session.
    const rec = el("div", { class: "remote-rec" }, [el("b", { text: "Recording" })]);
    rec.append(el("p", { class: "hint", text: "Each of you records only your own mic/camera. Recording your partner needs their permission, which lasts for this session only." }));
    const g = remote.remoteGrant;
    if (g === "granted") {
      rec.append(el("p", { text: "✓ Your partner allowed you to record their mic this session. Pick “🌐 Partner's mic” as an audio track's input, arm it and record." }));
      // Latency compensation for partner takes: live estimate + manual fine-tune.
      const est = el("p", { class: "hint", text: "Measuring the delay of your partner's audio…" });
      const fine = el("input", { attrs: { type: "number", step: "5", title: "Fine-tune (ms): raise it if partner takes still land late, lower it if early" } }) as HTMLInputElement;
      fine.value = String(partnerOffsetMs); fine.style.width = "70px";
      const show = async () => {
        remote.requestLatency();
        await new Promise((r) => setTimeout(r, 250));
        const c = partnerCompensation(await remote.delayStats(), null);
        est.textContent = `Partner takes are moved ≈ ${ms(c.total)} earlier: ${compText(c)}. The jitter buffer is re-checked during every take.`;
      };
      const remeasure = el("button", { text: "↻ Re-measure" }) as HTMLButtonElement;
      remeasure.title = "Send a short chirp through your partner's audio to time the connection again (they won't hear it; you may)";
      remeasure.addEventListener("click", async () => {
        remeasure.disabled = true; est.textContent = "Measuring…";
        const d = await remote.measurePartnerPath();
        if (d == null) toast("Couldn't measure the audio path — using an estimate. Try again in a quieter moment.", 6000);
        remeasure.disabled = false; void show();
      });
      fine.addEventListener("change", () => { partnerOffsetMs = Math.round(Number(fine.value) || 0); fine.value = String(partnerOffsetMs); localStorage.setItem("webcut.partnerOffsetMs", String(partnerOffsetMs)); void show(); });
      rec.append(el("b", { class: "remote-lat-title", text: "Latency compensation" }), est,
        el("div", { class: "remote-lat-row" }, [el("span", { class: "hint", text: "Fine-tune (ms)" }), fine, remeasure]));
      void show();
    }
    else if (g === "pending") rec.append(el("p", { class: "hint", text: "⏳ Waiting for your partner to allow or deny recording…" }));
    else {
      if (g === "denied") rec.append(el("p", { class: "hint", text: "Your partner declined. You can ask again." }));
      const ask = el("button", { text: "🎙 Request to record partner's mic" }) as HTMLButtonElement;
      ask.disabled = !remote.connected;
      ask.addEventListener("click", () => { if (remote.requestRecording()) { toast("Asked your partner for permission to record their mic."); document.querySelector(".modal-bg")?.remove(); } });
      rec.append(ask);
    }
    if (remote.grantedToPeer) {
      const revoke = el("button", { text: "Stop allowing partner to record me" });
      revoke.addEventListener("click", () => { remote.setGrantToPeer(false); toast("Your partner can no longer record your mic."); document.querySelector(".modal-bg")?.remove(); });
      rec.append(el("p", { text: "Your partner may record your mic this session." }), revoke);
    }
    body.append(rec);
    openModal("Remote session", body, [
      { label: "Leave session", primary: true, onClick: () => { remote.disconnect(); toast("Left remote session."); } },
      { label: "Close", onClick: () => {} },
    ]);
    return;
  }
  body.append(el("p", { class: "hint", text: "Live remote session: share a room code with a collaborator to sync transport + project edits and stream live audio (WebRTC)." }));
  const room = el("input", { attrs: { placeholder: "Room code (e.g. studio-42)" } }) as HTMLInputElement;
  room.style.width = "100%";
  body.append(room);
  openModal("Remote session", body, [
    { label: "Host", onClick: () => { remote.connect(room.value || "room", true); } },
    { label: "Join", primary: true, onClick: () => { remote.connect(room.value || "room", false); } },
  ]);
}
remote.onStatus = (s) => { syncBadge.textContent = s; syncBadge.classList.toggle("live", s === "connected"); };

// ---- remote recording permission (per session) ----------------------
recorder.remoteStream = () => (remote.remoteGrant === "granted" ? remote.remoteStream() : null);
// Our own audio round trip, so the partner's recordings of us line up.
remote.localLatency = () => recordCompensation();

// Partner side: while they record us, record our own mic locally too (raw,
// exact timing) and send it to them when they stop.
const localTakes = new Map<string, { rec: NonNullable<Awaited<ReturnType<typeof createCapture>>>; stream: MediaStream }>();
remote.onPartnerTakeStart = async (takeId) => {
  try {
    const deviceId = (remote as any).localStream?.getAudioTracks()[0]?.getSettings().deviceId as string | undefined;
    const nr = recorder.processing === "nr";
    const stream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: deviceId ? { exact: deviceId } : undefined, echoCancellation: false, noiseSuppression: nr, autoGainControl: false } });
    const rec = await createCapture(engine.ctx, stream);
    if (!rec) { stream.getTracks().forEach((t) => t.stop()); return; }
    rec.begin();
    localTakes.set(takeId, { rec, stream });
  } catch (e) { console.warn("[webcut] local capture for partner failed", e); }
};
remote.onPartnerTakeStop = async (takeId) => {
  const t = localTakes.get(takeId);
  if (!t) return;
  localTakes.delete(takeId);
  const buf = await t.rec.finish();
  const startedAt = t.rec.startedAt;
  t.rec.dispose(); t.stream.getTracks().forEach((x) => x.stop());
  if (buf && startedAt != null) await remote.sendPartnerTake(takeId, buf, startedAt, recordCompensation());
};

// Our side: the partner's full-quality take arrived — put it exactly where it
// belongs and swap it in for the network recording.
remote.onPartnerTakeProgress = (_id, frac) => { if (frac < 1) toast(`Receiving your partner's full-quality take… ${Math.round(frac * 100)}%`, 2500); };
remote.onPartnerTake = (takeId, buf, startedAt, latency) => {
  const p = pendingPartnerTakes.get(takeId);
  pendingPartnerTakes.delete(takeId);
  if (!p || remote.clockOffset == null) return;
  // Their first sample in our audio clock → our timeline, minus their device latency.
  const exactStart = p.anchor.pos + (startedAt - remote.clockOffset - p.anchor.at) - latency;
  let start = exactStart, offset = 0;
  if (start < 0) { offset = -start; start = 0; }
  const asset = store.addAsset(buf, `Partner take ${new Date().toLocaleTimeString()} (full quality)`);
  let swapped = 0, kept = 0;
  store.pushUndo();
  for (const c of p.clips) {
    const f = store.findClip(c.clipId);
    if (!f) continue;
    const cl = f.clip;
    const untouched = Math.abs(cl.start - c.placed.start) < 1e-6 && Math.abs(cl.offset - c.placed.offset) < 1e-6 && Math.abs(cl.duration - c.placed.duration) < 1e-6;
    if (!untouched) { kept++; continue; } // edited meanwhile: leave it; the new take is in Media
    Object.assign(cl, { assetId: asset.id, start, offset, duration: Math.max(0.02, buf.duration - offset), name: asset.name });
    swapped++;
  }
  store.emitChange("tracks");
  const shift = p.clips.length ? Math.round((store.findClip(p.clips[0].clipId)?.clip.start ?? exactStart) * 1000 - p.clips[0].placed.start * 1000) : 0;
  toast(swapped
    ? `Your partner's full-quality take arrived and replaced the call recording (exact timing${shift ? `, ${shift > 0 ? "+" : ""}${shift} ms vs the call version` : ""}).`
    : kept ? "Your partner's full-quality take arrived — you'd already edited the call recording, so it was added to Media instead." : "Your partner's full-quality take arrived (added to Media).", 9000);
};
/** The partner asks to record our mic → explicit Allow / Deny (this session only). */
remote.onRecRequest = () => new Promise<boolean>((resolve) => {
  let settled = false;
  const done = (v: boolean) => { if (!settled) { settled = true; resolve(v); } };
  openModal("Allow your partner to record you?", el("div", {}, [
    el("p", { text: "Your partner wants to record your microphone during this session." }),
    el("p", { class: "hint", text: "This lasts only until the session ends — you'll be asked again next time. You'll see a banner whenever they're recording you, and you can stop allowing it at any time from ⇄ Remote." }),
  ]), [
    { label: "Deny", onClick: () => done(false) },
    { label: "Allow for this session", primary: true, onClick: () => done(true) },
  ]);
  onBackdropCancel(() => done(false));
});
let lastGrant = remote.remoteGrant;
remote.onRecPermission = () => {
  const g = remote.remoteGrant;
  if (g !== lastGrant) {
    if (g === "granted") {
      toast("Your partner allowed recording their mic this session — choose “🌐 Partner's mic” as a track input.", 7000);
      void (async () => { // time their audio path so takes line up
        for (let i = 0; i < 30 && remote.clockOffset == null; i++) await new Promise((r) => setTimeout(r, 200));
        if (remote.remoteGrant === "granted") await remote.measurePartnerPath();
      })();
    }
    else if (g === "denied") toast("Your partner declined to be recorded.", 5000);
    else if (lastGrant === "granted") {
      toast("Recording your partner is no longer allowed (they revoked it or the session ended).", 7000);
      if (engine.recording && recordingPartner) void finishRecord(); // keep what was recorded until now
      // Tracks listening to the partner fall back to the default input.
      for (const t of store.project.tracks) if (t.inputDeviceId === REMOTE_INPUT) store.updateTrack(t.id, { inputDeviceId: null }, "tracks");
    }
    lastGrant = g;
  }
  pushDevices();
};
// A banner whenever the partner is recording OUR mic.
const peerRecBanner = el("div", { class: "rec-banner peer-rec-banner" }, [
  el("span", { class: "rec-dot" }), el("b", { text: "Your partner is recording your microphone" }),
]);
const stopAllowBtn = el("button", { class: "peer-rec-stop", text: "Stop allowing" });
stopAllowBtn.addEventListener("click", () => { remote.setGrantToPeer(false); toast("Your partner can no longer record your mic."); });
peerRecBanner.append(stopAllowBtn);
peerRecBanner.style.display = "none";
document.body.append(peerRecBanner);
remote.onPeerRecording = (on) => { peerRecBanner.style.display = on ? "flex" : "none"; };

// ---- render loop -----------------------------------------------------
function tick() {
  timeline.draw();
  monitor.render(engine.position(), engine.playing);
  const lvls = engine.levels();
  mixer.updateMeters(lvls);
  // merge recorder input levels into head meters
  const recLvls = recorder.levels();
  const headLvls: Record<string, number> = { ...lvls.tracks };
  for (const k in recLvls) headLvls[k] = Math.max(headLvls[k] ?? 0, recLvls[k]);
  timeline.updateMeters(headLvls);
  const pos = engine.position();
  timeline.followPlayhead(pos, engine.playing);
  // live recording waveform overlay (placed at the latency-compensated start so it
  // doesn't jump when the take finalizes)
  if (engine.recording) {
    const start = Math.max(0, recordStartPos - recordCompensation());
    const partnerStart = Math.max(0, recordStartPos - (lastPartnerComp?.total ?? partnerCompensation(null).total));
    const tracks: Record<string, any> = {};
    for (const [id, pk] of recorder.recordingPeaks()) tracks[id] = { startSec: partnerTrackIds.has(id) ? partnerStart : start, min: pk.min, max: pk.max, binSamples: pk.binSamples };
    timeline.setRecordOverlay({ sampleRate: engine.ctx.sampleRate, tracks });
  } else {
    timeline.setRecordOverlay(null);
  }
  requestAnimationFrame(tick);
}
requestAnimationFrame(tick);

engine.onTransport = () => { tPlay.textContent = engine.playing ? "⏸" : "▶"; };
store.on("change", ({ scope }) => {
  if (scope === "tracks" || scope === "project") refreshArm();
  if (scope === "tracks" || scope === "mixer") for (const t of store.project.tracks) recorder.setInputGain(t.id, t.inputGain ?? 0); // live input-gain
  if (scope === "mixer" || scope === "project" || scope === "tracks") {
    const ids = new Set<string>();
    for (const t of store.project.tracks) for (const f of t.fx) ids.add(f.id);
    for (const f of store.project.masterFx) ids.add(f.id);
    reconcileWam(engine, ids);
  }
  if (scope === "project") { projName.value = store.project.name; recModeSel.value = store.project.recordMode ?? "comp"; }
  if (scope === "project" || scope === "transport") loopBtn.classList.toggle("on", store.project.loopEnabled);
});

// ---- keyboard --------------------------------------------------------
// J/K/L shuttle: L plays, K stops, J scrubs backward (press again to go faster).
let reverseTimer: any = null, reverseRate = 0;
function stopReverse() { clearInterval(reverseTimer); reverseTimer = null; reverseRate = 0; }
function shuttleReverse() {
  if (engine.playing) engine.pause();
  reverseRate = reverseRate ? Math.min(8, reverseRate * 2) : 1;
  if (reverseTimer) return;
  let last = performance.now();
  reverseTimer = setInterval(() => {
    const now = performance.now(), dt = (now - last) / 1000; last = now;
    const p = engine.position() - dt * reverseRate;
    engine.seek(Math.max(0, p));
    if (p <= 0) stopReverse();
  }, 40);
}
const frameDur = () => 1 / (store.project.fps || 30);
/** Selected clip ids, or undefined (= "every clip under the playhead"). */
const selOrAll = () => (store.selectedClipIds.size ? [...store.selectedClipIds] : undefined);
function clipsUnderPlayhead(): string[] {
  const pos = engine.position();
  return store.project.tracks.flatMap((t) => t.clips.filter((c) => c.takeActive !== false && pos > c.start && pos < c.start + c.duration).map((c) => c.id));
}
function jumpEditPoint(dir: 1 | -1) {
  const pos = engine.position(), pts = store.editPoints();
  const t = dir > 0 ? pts.find((p) => p > pos + 1e-4) : [...pts].reverse().find((p) => p < pos - 1e-4);
  if (t != null) engine.seek(t);
}

window.addEventListener("keydown", (e) => {
  // Esc stops an in-progress camera recording (works even from inputs).
  if (e.key === "Escape" && camRec) { e.preventDefault(); toggleVideoRecord(); return; }
  const tag = (e.target as HTMLElement)?.tagName;
  if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
  if (document.querySelector(".modal-bg")) return; // dialogs own the keyboard
  const mod = e.ctrlKey || e.metaKey;
  const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
  const handled = () => e.preventDefault();

  if (mod) {
    if (k === "z" && !e.shiftKey) { handled(); store.undo(); }
    else if (k === "y" || (k === "z" && e.shiftKey)) { handled(); store.redo(); }
    else if (k === "s") { handled(); saveToFile(e.shiftKey); }
    else if (k === "o") { handled(); openFromFile(); }
    else if (k === "d") { handled(); if (store.selectedClipIds.size) store.duplicateClips(); }
    else if (k === "a") { handled(); store.setClipSelection(store.project.tracks.flatMap((t) => t.clips.filter((c) => c.takeActive !== false).map((c) => c.id))); }
    else if (k === "c") { handled(); const n = store.copyClips(); if (n) toast(`Copied ${n} clip${n > 1 ? "s" : ""}.`); }
    else if (k === "x") { handled(); const n = store.copyClips(); if (n) { store.removeClips(); toast(`Cut ${n} clip${n > 1 ? "s" : ""}.`); } }
    else if (k === "v") { handled(); if (!store.pasteClips(engine.position())) toast("Nothing to paste — copy clips first (Ctrl+C)."); }
    else if (k === "k") { handled(); store.splitClipsAt(engine.position(), selOrAll() ?? clipsUnderPlayhead()); }
    else if (k === "t") { handled(); addTransitionAtPlayhead(); }
    else if (k === "r") { handled(); if (store.selectedClipId) openSpeedDialog(store, store.selectedClipId); else toast("Select a clip to change its speed."); }
    return;
  }

  if (e.code === "Space") { handled(); stopReverse(); togglePlay(); }
  else if (k === "l") { handled(); stopReverse(); if (!engine.playing) togglePlay(); }
  else if (k === "k") { handled(); stopReverse(); if (engine.playing) engine.pause(); }
  else if (k === "j") { handled(); shuttleReverse(); }
  else if (k === "r") { handled(); toggleRecord(); }
  else if (k === "v") timeline.setTool("select");
  else if (k === "c") timeline.setTool("blade");
  else if (k === "b") timeline.setTool("ripple");
  else if (k === "y") timeline.setTool("slip");
  else if (k === "n") { timeline.setSnap(!timeline.snap); toast(`Snapping ${timeline.snap ? "on" : "off"}.`, 1200); }
  else if (k === "s") store.splitClipsAt(engine.position(), selOrAll() ?? clipsUnderPlayhead());
  else if (k === "q") { if (!store.trimToTime(engine.position(), "start", true, selOrAll())) toast("Put the playhead inside a clip to trim (Q)."); }
  else if (k === "w") { if (!store.trimToTime(engine.position(), "end", true, selOrAll())) toast("Put the playhead inside a clip to trim (W)."); }
  else if (k === "i") markAt("in");
  else if (k === "o") markAt("out");
  else if (k === "x" && e.altKey) { store.setMark("in", undefined); store.setMark("out", undefined); }
  else if (k === ";") liftOrExtract(false);
  else if (k === "'") liftOrExtract(true);
  else if (k === "m") { store.addMarker(engine.position()); toast("Marker added.", 1200); }
  else if (k === "e" && e.shiftKey) store.toggleClipsEnabled();
  else if (k === "Delete" || k === "Backspace") { if (store.selectedClipIds.size) { if (e.shiftKey) store.rippleDelete(); else store.removeClips(); } }
  else if (k === "ArrowLeft" || k === "ArrowRight") {
    handled();
    const dir = k === "ArrowRight" ? 1 : -1;
    const frames = e.shiftKey ? 10 : 1;
    if (e.altKey) store.nudgeClips(dir * frames * frameDur()); // nudge selected clips
    else { stopReverse(); const f = frameDur(); engine.seek(Math.max(0, (Math.round(engine.position() / f) + dir * frames) * f)); }
  }
  else if (k === "ArrowUp") { handled(); jumpEditPoint(-1); }
  else if (k === "ArrowDown") { handled(); jumpEditPoint(1); }
  else if (k === "Home") { handled(); engine.seek(0); }
  else if (k === "End") { handled(); engine.seek(projectEnd()); }
  else if (k === "=" || k === "+") timeline.zoomBy(1.5);
  else if (k === "-" || k === "_") timeline.zoomBy(1 / 1.5);
  else if (k === "\\") timeline.zoomToFit();
  else if (k === "?") openShortcuts();
  else if (k === "Escape") { if (timeline.tool !== "select") timeline.setTool("select"); else store.setClipSelection([]); }
});

function openShortcuts() {
  const groups: [string, [string, string][]][] = [
    ["Playback", [["Space", "Play / pause"], ["J / K / L", "Shuttle back / stop / play"], ["← / →", "Previous / next frame (Shift: 10)"], ["↑ / ↓", "Previous / next edit point"], ["Home / End", "Start / end"]]],
    ["Tools", [["V", "Select"], ["C", "Blade (Shift+click cuts all tracks)"], ["B", "Ripple trim"], ["Y", "Slip"], ["N", "Snapping on/off (Alt inverts while dragging)"]]],
    ["Editing", [["S / Ctrl+K", "Split at playhead (selected, else all)"], ["Q / W", "Ripple-trim start / end to playhead"], ["Delete", "Delete"], ["Shift+Delete", "Ripple delete"], ["Alt+← / →", "Nudge selected 1 frame (Shift: 10)"], ["Shift+E", "Enable / disable clip"], ["Ctrl+T", "Cross-dissolve at nearest cut"], ["Ctrl+R", "Speed / duration"], ["Ctrl+C / X / V", "Copy / cut / paste at playhead"], ["Ctrl+D", "Duplicate"], ["Ctrl+A", "Select all"]]],
    ["Marks", [["I / O", "Mark In / Out"], ["Alt+X", "Clear In/Out"], [";", "Lift In→Out"], ["'", "Extract In→Out"], ["M", "Add marker"]]],
    ["View & file", [["= / −", "Zoom in / out"], ["\\", "Zoom to fit"], ["Ctrl+wheel", "Zoom at pointer"], ["Ctrl+Z / Y", "Undo / redo"], ["Ctrl+S", "Save (Shift: Save As)"], ["R", "Record audio"], ["?", "This list"]]],
  ];
  const body = el("div", { class: "shortcuts" });
  for (const [title, rows] of groups) {
    const sec = el("div", { class: "sc-group" }, [el("h4", { text: title })]);
    for (const [key, what] of rows) sec.append(el("div", { class: "sc-row" }, [el("kbd", { text: key }), el("span", { text: what })]));
    body.append(sec);
  }
  body.append(el("p", { class: "hint sc-foot", text: "Right-click a clip, an empty lane or the ruler for more (close gap, select forward, markers, In/Out)." }));
  openModal("Keyboard shortcuts", body, [{ label: "Close", primary: true, onClick: () => {} }]);
}

// ---- boot ------------------------------------------------------------
(async () => {
  // Register the real-time denoise worklet so the "Denoise (profile)" insert can
  // be created synchronously when added to a track.
  try { await engine.ctx.audioWorklet.addModule(DENOISE_WORKLET_URL); } catch (e) { console.warn("denoise worklet unavailable", e); }
  // Same for the "Deverb (room echo)" insert.
  try { await engine.ctx.audioWorklet.addModule(DEVERB_WORKLET_URL); } catch (e) { console.warn("deverb worklet unavailable", e); }
  // Do NOT enumerate inputs here — that would prompt for mic access at startup.
  // Inputs are enumerated lazily on first arm/record (see ensureInputDevices).
  // Output enumeration does not require permission, so it's safe at boot.
  try { mixer.setOutputs(await listOutputs()); } catch {}
})();

// We do NOT register a service worker (sw.js is now a kill-switch that cleans up
// any stale install from earlier versions). Proactively unregister + clear caches
// so a previously-cached worker can't serve stale code and block boot.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations?.().then((rs) => rs.forEach((r) => r.unregister())).catch(() => {});
  (globalThis as any).caches?.keys?.().then((ks: string[]) => ks.forEach((k) => caches.delete(k))).catch(() => {});
}

// resume audio on first gesture (autoplay policy)
window.addEventListener("pointerdown", () => engine.resume(), { once: true });

for (const b of Array.from(document.querySelectorAll<HTMLButtonElement>("button[title]")))
  if (!b.getAttribute("aria-label") && !/[A-Za-z]/.test(b.textContent || "")) b.setAttribute("aria-label", b.title);

(window as any).webcut = { files: { adopt: adoptOpenedProject, save: saveToFile, isDirty, confirmDiscard, get handle() { return fileHandle; } }, store, engine, remote, timeline, recorder, monitor, importVideoToMedia, placeAssetOnTimeline, extractAudioBuffers, demuxMp4Audio, demuxMatroskaAudio, exportVideo, exportWebcut, importWebcut };
